import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import $ from "jquery";
import "select2/dist/css/select2.min.css";
import "select2";
import Select2Dropdown from "../components/common/Select2Dropdown";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import { masterDataAPI, approvalWorkflowAPI, productMasterAPI, categoryMasterAPI } from "../services/api";
import "./DynamicApprovalConfigurationLevelSetup.css";
import "./RaiseComplaint.css";
import "./UserManagement.css";
import "./AddEditUser.css";
import "./DynamicApprovalConfiguration.css";

/** Parse Approval workflow extension from WorkflowSource JSON (if present). */
const parseUserSpecificationFromWorkflowSource = (raw) => {
  if (typeof raw !== "string" || !raw.trim().startsWith("{")) return "None";
  try {
    const o = JSON.parse(raw);
    const s = o.userSpecificationType ?? o.UserSpecificationType;
    if (s == null || String(s).trim() === "") return "None";
    const t = String(s).trim();
    const norm = t === "LogHead" ? "CPD" : t;
    return ["None", "Plant", "RDC", "BCM", "CPD", "General"].includes(norm) ? norm : "None";
  } catch {
    return "None";
  }
};

/** Domestic (M001) vs Export (M002) from market code / name. */
const resolveMarketKind = (marketCode, marketName) => {
  const c = String(marketCode || "").trim().toUpperCase();
  const n = String(marketName || "").trim().toUpperCase();
  if (c === "M001" || n === "DOMESTIC" || c.includes("DOMESTIC") || (c.includes("DOM") && !c.includes("EXP"))) {
    return "Domestic";
  }
  if (c === "M002" || n === "EXPORT" || c.includes("EXPORT") || c.includes("EXP")) {
    return "Export";
  }
  return "";
};

const USER_SPEC_OPTIONS_DOMESTIC = [
  { id: "Plant", text: "Plant" },
  { id: "RDC", text: "RDC" },
  { id: "BCM", text: "BCM" },
  { id: "General", text: "General" },
];

const USER_SPEC_OPTIONS_EXPORT = [
  { id: "Plant", text: "Plant" },
  { id: "CPD", text: "CPD" },
  { id: "General", text: "General" },
];

/** SR Journey Flow choices for Approval Configuration from selected market(s). */
const getUserSpecificationOptionsForMarkets = (marketCodes, marketsList) => {
  const codes = Array.isArray(marketCodes)
    ? marketCodes.map((c) => String(c).trim()).filter(Boolean)
    : [];
  if (!codes.length) return [];

  const kinds = new Set();
  for (const mc of codes) {
    const m = (marketsList || []).find(
      (x) =>
        String(x.marketCode || x.MARKETCODE || x.MarketCode || "").trim().toUpperCase() ===
        mc.toUpperCase()
    );
    const kind = resolveMarketKind(
      mc,
      m?.marketName || m?.MARKETNAME || m?.MarketName || ""
    );
    if (kind) kinds.add(kind);
  }

  if (kinds.has("Domestic") && kinds.has("Export")) {
    const byId = new Map();
    [...USER_SPEC_OPTIONS_DOMESTIC, ...USER_SPEC_OPTIONS_EXPORT].forEach((o) =>
      byId.set(o.id, o)
    );
    return [...byId.values()];
  }
  if (kinds.has("Export")) return [...USER_SPEC_OPTIONS_EXPORT];
  if (kinds.has("Domestic")) return [...USER_SPEC_OPTIONS_DOMESTIC];
  return [];
};

/** Resolve user role code from row value + options (code, display name, or object). */
function resolveUserRoleCode(userRoleValue, userRoleOptions = []) {
  if (userRoleValue == null || userRoleValue === "") return null;
  const opts = Array.isArray(userRoleOptions) ? userRoleOptions : [];

  const matchOption = (candidate) => {
    const c = String(candidate ?? "").trim();
    if (!c || c === "0000") return null;
    const byId = opts.find((r) => {
      const id = String(r.id ?? r.userTypeCode ?? "").trim();
      return id === c || id.toUpperCase() === c.toUpperCase();
    });
    if (byId) return String(byId.id ?? byId.userTypeCode).trim();
    const byName = opts.find((r) => {
      const name = String(r.name ?? r.userTypeName ?? "")
        .trim()
        .toLowerCase();
      return name && name === c.toLowerCase();
    });
    if (byName) return String(byName.id ?? byName.userTypeCode).trim();
    return c;
  };

  if (typeof userRoleValue === "string") return matchOption(userRoleValue);
  if (typeof userRoleValue === "number") {
    return userRoleValue === 0 ? null : String(userRoleValue);
  }
  if (typeof userRoleValue === "object") {
    const fromFields = userRoleValue.id ?? userRoleValue.userTypeCode;
    if (fromFields) return matchOption(fromFields);
    const fromName = userRoleValue.name ?? userRoleValue.userTypeName;
    if (fromName) return matchOption(fromName);
  }
  return null;
}

/**
 * DynamicApprovalConfigurationLevelSetup Component
 * Manages dynamic approval configuration levels with drag-and-drop reordering,
 * row insertion/deletion, and configurable fields per row.
 */
const DynamicApprovalConfigurationLevelSetup = () => {
  const navigate = useNavigate();
  const { workflowCode } = useParams();
  const location = useLocation();
  // Get PageType from URL search params or default
  const searchParams = new URLSearchParams(location.search);
  const initialPageType = searchParams.get('pageType') || 'ApprovalConfiguration';
  
  // Edit mode: if workflowCode exists in URL params
  const isEditMode = !!workflowCode;
  // Add mode: if no workflowCode in URL params
  const isAddMode = !workflowCode;
  
  // Page Type state
  const [pageType, setPageType] = useState(initialPageType);
  
  // Workflow form states
  const [workflowForm, setWorkflowForm] = useState({
    workflowCode: "",
    workflowName: "",
    srTypeCode: "",
    marketCode: "0000",
    marketCodes: [], // ApprovalConfiguration: single market in UI (stored as one-element array)
    catgCodes: [], // Array for multiple categories / business vertical codes
    pageType: initialPageType, // Add PageType field
    userSpecificationType: "None",
    workflowSource: "",
    slaDays: "",
    tatDays: "",
    isActive: true,
  });
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [showWorkflowForm, setShowWorkflowForm] = useState(true);
  const [markets, setMarkets] = useState([]);
  const [categories, setCategories] = useState([]);
  /** ApprovalConfiguration: CATEGORYMASTER rows merged for selected markets (same as Add User BV). */
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [srTypeOptions, setSrTypeOptions] = useState([]);
  
  // Workflow form refs
  const marketSelectRef = useRef(null);
  const catgSelectRef = useRef(null);
  const isSettingInitialMarketRef = useRef(false);
  /** Add mode: avoid re-applying “select all SR types” after user clears selection (key = workflowCode|market). */
  const lastSrTypeAutoFillKeyRef = useRef("");
  /** Ignore stale getSRTypes responses when market changes quickly or mount no-market call races. */
  const loadSRTypesSeqRef = useRef(0);
  
  // Level setup states
  const [rows, setRows] = useState([]);
  const rowsRef = useRef([]);
  const [userRoleOptions, setUserRoleOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  /** Index of row whose + was clicked — brief loader while inserting (no API on insert). */
  const [insertingRowAfterIndex, setInsertingRowAfterIndex] = useState(null);
  /** Row id while opposite-page role API check runs (on User Role change). */
  const [validatingRoleRowId, setValidatingRoleRowId] = useState(null);
  /** Cached map of role codes used on opposite pageType workflows (one scan, parallel level loads). */
  const oppositePageRolesCacheRef = useRef({
    pageType: "",
    at: 0,
    roleToWorkflow: null,
  });
  const oppositePageRolesLoadPromiseRef = useRef(null);
  const select2Refs = useRef({});

  const approvalUserSpecOptions = useMemo(() => {
    if (pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration") return [];
    return getUserSpecificationOptionsForMarkets(workflowForm.marketCodes, markets);
  }, [pageType, workflowForm.marketCodes, markets]);

  // Keep SR Journey Flow valid when market changes (Approval / SLA-TAT level setup)
  useEffect(() => {
    if (pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration") return;
    const allowed = new Set(approvalUserSpecOptions.map((o) => o.id));
    setWorkflowForm((prev) => {
      const current = String(prev.userSpecificationType || "").trim();
      if (!current || current === "None" || allowed.has(current)) return prev;
      return { ...prev, userSpecificationType: approvalUserSpecOptions[0]?.id || "" };
    });
  }, [pageType, approvalUserSpecOptions]);

  // Update PageType when URL search params change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlPageType = searchParams.get('pageType') || 'ApprovalConfiguration';
    if (urlPageType !== pageType) {
      setPageType(urlPageType);
      setWorkflowForm((prev) => ({ ...prev, pageType: urlPageType }));
    }
  }, [location.search, pageType]);

  // Load workflow data on mount (single loading cycle — avoids race clearing loading early)
  useEffect(() => {
    let cancelled = false;
    const initializePage = async () => {
      setLoading(true);
      try {
        await loadMarkets();
        await loadUserRoles();
        if (isAddMode) {
          await loadNextWorkflowCode();
        } else if (isEditMode && workflowCode) {
          await loadWorkflowForEdit();
        }
      } catch (error) {
        console.error("Error initializing level setup page:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    initializePage();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowCode, isEditMode, isAddMode]);

  // Load categories when market code changes (SLA / legacy path — not used for ApprovalConfiguration BV)
  useEffect(() => {
    if (pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration") return;
    if (workflowForm.marketCode && workflowForm.marketCode !== "0000") {
      loadCategories(workflowForm.marketCode);
    } else {
      setCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowForm.marketCode, pageType]);

  // ApprovalConfiguration & SLA/TAT: Business Vertical options from CATEGORYMASTER per selected market(s) — same as /users/add
  useEffect(() => {
    if (pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration") return;
    const codes = workflowForm.marketCodes || [];
    if (!codes.length) {
      setBusinessVerticals([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = [];
        const seen = new Set();
        for (const marketCode of codes) {
          const catRes = await categoryMasterAPI.getPaged({
            marketCode,
            isActive: true,
            pageNumber: 1,
            pageSize: 5000,
          });
          const cats =
            catRes?.data?.Categories ||
            catRes?.data?.categories ||
            catRes?.data?.data?.Categories ||
            [];
          if (Array.isArray(cats)) {
            for (const c of cats) {
              const id = c.catgCode || c.CATGCODE || c.CatgCode || "";
              const key = String(id).trim().toUpperCase();
              if (key && !seen.has(key)) {
                seen.add(key);
                merged.push(c);
              }
            }
          }
        }
        if (!cancelled) setBusinessVerticals(merged);
      } catch (e) {
        console.error("Failed to load business verticals for markets", codes, e);
        if (!cancelled) setBusinessVerticals([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType, (workflowForm.marketCodes || []).join("|")]);

  // Load SR Types (sequenced so a late no-market response cannot overwrite market-filtered results)
  const loadSRTypes = async (marketCode = null) => {
    const seq = ++loadSRTypesSeqRef.current;
    try {
      const srTypesRes = await masterDataAPI.getSRTypes(marketCode, true);
      if (seq !== loadSRTypesSeqRef.current) return;

      let srTypes = [];
      if (srTypesRes?.success && srTypesRes.data != null) {
        if (Array.isArray(srTypesRes.data)) {
          srTypes = srTypesRes.data;
        } else if (Array.isArray(srTypesRes.data?.SRTypes)) {
          srTypes = srTypesRes.data.SRTypes;
        }
      } else if (srTypesRes?.data && Array.isArray(srTypesRes.data)) {
        srTypes = srTypesRes.data;
      } else if (Array.isArray(srTypesRes)) {
        srTypes = srTypesRes;
      }

      if (seq !== loadSRTypesSeqRef.current) return;

      if (srTypes.length > 0) {
        const mappedOptions = srTypes
          .map((st) => {
            const code = String(
              st.SRTypeCode || st.SRTYPECODE || st.srTypeCode || st.SrTypeCode || ""
            ).trim();
            const rawName =
              st.Name ||
              st.NAME ||
              st.name ||
              st.SRTypeName ||
              st.sRTypeName ||
              st.Description ||
              st.description ||
              "";
            const text = String(rawName).trim() || code;
            return { id: code, text };
          })
          .filter((st) => st.id);

        setSrTypeOptions(mappedOptions);
      } else {
        setSrTypeOptions([]);
      }
    } catch (error) {
      if (seq !== loadSRTypesSeqRef.current) return;
      console.error("Error loading SR Types:", error);
      setSrTypeOptions([]);
    }
  };

  // ApprovalConfiguration & SLA/TAT: SR types follow first selected market (API expects a single market filter)
  useEffect(() => {
    if (pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration") return;
    const first = String(workflowForm.marketCodes?.[0] || "").trim();
    if (first && first !== "0000") loadSRTypes(first);
    else loadSRTypes(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType, (workflowForm.marketCodes || []).join("|")]);

  // Initialize Select2 for Market dropdown (legacy single-market flows only)
  useEffect(() => {
    if (!showWorkflowForm) return;
    if (pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration") return;
    if (marketSelectRef.current && markets.length > 0) {
      const timer = setTimeout(() => {
        const $select = $(marketSelectRef.current);
        if (!$select.length) return;
        
        const isSelect2Initialized = $select.data("select2");
        const shouldSetValue = workflowForm.marketCode && workflowForm.marketCode !== "0000";
        const currentVal = $select.val();
        
        if (isSelect2Initialized) {
          if (shouldSetValue && currentVal !== workflowForm.marketCode) {
            const hasCategories = workflowForm.catgCodes && workflowForm.catgCodes.length > 0;
            if (hasCategories) {
              isSettingInitialMarketRef.current = true;
            }
            $select.val(workflowForm.marketCode).trigger("change");
          }
          return;
        }
        
        $select.select2({
          width: "100%",
          placeholder: "Select Market",
          allowClear: false,
        });
        
        $select.on("change", function (e) {
          const selectedMarketCode = e.target.value || "0000";
          const isInitialLoad = isSettingInitialMarketRef.current;
          
          setWorkflowForm((prev) => ({ 
            ...prev, 
            marketCode: selectedMarketCode,
            catgCodes: isInitialLoad ? prev.catgCodes : [],
            srTypeCode: ""
          }));
          
          isSettingInitialMarketRef.current = false;
          
          if (selectedMarketCode && selectedMarketCode !== "0000") {
            loadCategories(selectedMarketCode);
            loadSRTypes(selectedMarketCode);
          } else {
            setSrTypeOptions([]);
          }
        });
        
        if (shouldSetValue) {
          setTimeout(() => {
            if ($select.val() !== workflowForm.marketCode) {
              isSettingInitialMarketRef.current = true;
              $select.val(workflowForm.marketCode).trigger("change");
            }
          }, 100);
        }
      }, 150);
      
      return () => {
        clearTimeout(timer);
        if (marketSelectRef.current) {
          const $select = $(marketSelectRef.current);
          if ($select.data("select2")) {
            try {
              $select.off("change");
              $select.select2("destroy");
            } catch (e) {
              // Ignore cleanup errors
            }
          }
        }
      };
    }
  }, [markets, workflowForm.marketCode, showWorkflowForm, pageType]);

  // Initialize Select2 for Category multi-select dropdown
  useEffect(() => {
    if (pageType === "SLATatConfiguration") return;
    if (pageType === "ApprovalConfiguration") return;
    if (!showWorkflowForm) return;
    if (!catgSelectRef.current) return;
    
    const timer = setTimeout(() => {
      const $select = $(catgSelectRef.current);
      if (!$select.length) return;
      
      const isDisabled = !workflowForm.marketCode || workflowForm.marketCode === "0000";
      const isSelect2Initialized = $select.data("select2");
      
      if (isSelect2Initialized) {
        try {
          $select.off("change");
          $select.select2("destroy");
        } catch (e) {
          console.warn("Error destroying Select2:", e);
        }
      }
      
      $select.prop("disabled", isDisabled);
      
      // Add "Select all" option at the beginning if not already present
      const $existingSelectAll = $select.find('option[value="__select_all__"]');
      if ($existingSelectAll.length === 0) {
        $select.prepend('<option value="__select_all__">Select all</option>');
      }

      $select.select2({
        width: "100%",
        placeholder: isDisabled ? "Please select Market first" : "Select Product Categories",
        allowClear: true,
        multiple: true,
        closeOnSelect: false, // Keep dropdown open when selecting
        minimumResultsForSearch: Infinity, // Hide search box
        templateResult: function (data) {
          if (!data.id) {
            return data.text;
          }
          const inputId = "category-option-" + data.id;
          const $result = $(
            '<label class="category-option-with-checkbox"></label>'
          ).attr("for", inputId);
          
          // Also handle clicks on the label itself
          $result.on("click", function(e) {
            // Only handle if clicking on the label, not the checkbox (checkbox has its own handler)
            if ($(e.target).is(".category-checkbox")) {
              return;
            }
            
            e.preventDefault();
            e.stopPropagation();
            
            const optId = String(data.id);
            console.log("Label clicked for:", optId);
            
            // Get all available category codes from React state
            const allCatCodes = categories.map(cat => {
              const code = cat.catgCode || cat.CatgCode || "";
              return String(code);
            }).filter(code => code);
            
            // Get current state from Select2 values
            const currentVals = ($select.val() || []).map(v => String(v));
            const catCodesOnly = currentVals.filter(v => v !== '__select_all__');

            // Determine current selection state
            let isCurrSelected = false;
            if (optId === '__select_all__') {
              isCurrSelected = allCatCodes.length > 0 && 
                             allCatCodes.every(code => catCodesOnly.includes(code));
            } else {
              isCurrSelected = catCodesOnly.includes(optId);
            }

            // Toggle: if currently selected, deselect; if not selected, select
            let newVals = [];

            if (optId === '__select_all__') {
              if (isCurrSelected) {
                newVals = [];
              } else {
                if (allCatCodes.length > 0) {
                  newVals = ['__select_all__', ...allCatCodes];
                } else {
                  newVals = [];
                }
              }
            } else {
              if (isCurrSelected) {
                newVals = catCodesOnly.filter(v => v !== optId);
              } else {
                newVals = [...catCodesOnly, optId];
                if (allCatCodes.length > 0 && allCatCodes.every(code => newVals.includes(code))) {
                  newVals = ['__select_all__', ...newVals];
                }
              }
            }

            console.log("Label handler - Setting new values:", newVals);
            
            // Update Select2 selection
            $select.val(newVals);
            
            // Manually update all checkboxes immediately
            const newCatCodesOnly = newVals.filter(v => v !== '__select_all__');
            
            // Update all checkboxes in the dropdown
            $(".select2-results__option").each(function() {
              const $opt = $(this);
              const $cb = $opt.find(".category-checkbox");
              if ($cb.length) {
                const cbVal = String($cb.data("value") || "");
                if (cbVal === '__select_all__') {
                  const shouldBeChecked = allCatCodes.length > 0 && 
                                        allCatCodes.every(code => newCatCodesOnly.includes(code));
                  $cb.prop("checked", shouldBeChecked);
                } else {
                  $cb.prop("checked", newCatCodesOnly.includes(cbVal));
                }
              }
            });
            
            // Trigger change event
            $select.trigger("change");
            
            return false;
          });
          const currentValues = ($select.val() || []).map(v => String(v));
          const categoryCodesOnly = currentValues.filter(v => v !== '__select_all__');
          let isSelected = false;
          
          if (String(data.id) === '__select_all__') {
            // "Select all" should be checked if all categories are selected
            const allCategoryCodes = categories.map(cat => {
              const code = cat.catgCode || cat.CatgCode || "";
              return String(code);
            }).filter(code => code);
            isSelected = allCategoryCodes.length > 0 && 
                        allCategoryCodes.every(code => categoryCodesOnly.includes(code));
          } else {
            isSelected = categoryCodesOnly.includes(String(data.id));
          }
          
          const $checkbox = $('<input type="checkbox" id="' +
              inputId +
              '" class="category-checkbox" ' +
              (isSelected ? "checked" : "") +
              ' style="margin-right: 8px;" data-value="' +
              data.id +
              '" />');
          
          // Attach click handler directly to this checkbox
          $checkbox.on("mousedown", function(e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            const $cb = $(this);
            const optId = String($cb.data("value") || "");
            
            console.log("Direct checkbox mousedown:", optId);
            
            if (!optId) return false;
            
            // Get all available category codes from React state
            const allCatCodes = categories.map(cat => {
              const code = cat.catgCode || cat.CatgCode || "";
              return String(code);
            }).filter(code => code);
            
            // Get current state from Select2 values
            const currentVals = ($select.val() || []).map(v => String(v));
            const catCodesOnly = currentVals.filter(v => v !== '__select_all__');

            // Determine current selection state
            let isCurrSelected = false;
            if (optId === '__select_all__') {
              isCurrSelected = allCatCodes.length > 0 && 
                             allCatCodes.every(code => catCodesOnly.includes(code));
            } else {
              isCurrSelected = catCodesOnly.includes(optId);
            }

            // Toggle: if currently selected, deselect; if not selected, select
            let newVals = [];

            if (optId === '__select_all__') {
              if (isCurrSelected) {
                newVals = [];
              } else {
                if (allCatCodes.length > 0) {
                  newVals = ['__select_all__', ...allCatCodes];
                } else {
                  newVals = [];
                }
              }
            } else {
              if (isCurrSelected) {
                newVals = catCodesOnly.filter(v => v !== optId);
              } else {
                newVals = [...catCodesOnly, optId];
                if (allCatCodes.length > 0 && allCatCodes.every(code => newVals.includes(code))) {
                  newVals = ['__select_all__', ...newVals];
                }
              }
            }

            console.log("Setting new values:", newVals);
            
            // Update Select2 selection
            $select.val(newVals);
            
            // Manually update all checkboxes immediately
            const newCatCodesOnly = newVals.filter(v => v !== '__select_all__');
            
            // Update all checkboxes in the dropdown
            $(".select2-results__option").each(function() {
              const $opt = $(this);
              const $cb = $opt.find(".category-checkbox");
              if ($cb.length) {
                const cbVal = String($cb.data("value") || "");
                if (cbVal === '__select_all__') {
                  const shouldBeChecked = allCatCodes.length > 0 && 
                                        allCatCodes.every(code => newCatCodesOnly.includes(code));
                  $cb.prop("checked", shouldBeChecked);
                } else {
                  $cb.prop("checked", newCatCodesOnly.includes(cbVal));
                }
              }
            });
            
            // Trigger change event
            $select.trigger("change");
            
            return false;
          });
          
          $result.append($checkbox);
          $result.append(
            '<span style="cursor: pointer;">' + data.text + "</span>"
          );
          return $result;
        },
        templateSelection: function () {
          // Return empty to prevent showing individual selections
          return "";
        },
      });

      const updateSelectionDisplay = function () {
        const selectedValues = $select.val() || [];
        const selectedCount = selectedValues.filter(v => v !== '__select_all__').length;
        const $selection = $select
          .next(".select2-container")
          .find(".select2-selection__rendered");

        if (selectedCount > 0) {
          $selection.html(
            '<span class="category-selection-count">All selected (' +
              selectedCount +
              ")</span>"
          );
        } else {
          $selection.html("");
        }
      };

      // Ensure arrow is visible for Category dropdown
      setTimeout(() => {
        const $categoryContainer = $select.next(".select2-container");
        if ($categoryContainer.length) {
          const $categoryArrow = $categoryContainer.find(
            ".select2-selection__arrow"
          );
          if (
            $categoryArrow.length === 0 ||
            $categoryArrow.find("b").length === 0
          ) {
            $categoryArrow.html('<b role="presentation"></b>');
          }
          $categoryArrow.css({
            height: "42px",
            right: "12px",
            width: "20px",
            display: "block",
            position: "relative",
          });
          const $arrowB = $categoryArrow.find("b");
          if ($arrowB.length) {
            $arrowB.css({
              borderColor: "#888 transparent transparent transparent",
              borderStyle: "solid",
              borderWidth: "5px 4px 0 4px",
              height: "0",
              left: "50%",
              marginLeft: "-4px",
              marginTop: "-2px",
              position: "absolute",
              top: "50%",
              width: "0",
              display: "block",
            });
          }
        }
      }, 100);

      // Initial display update after Select2 is initialized
      setTimeout(() => {
        updateSelectionDisplay();
      }, 50);

      // Prevent Select2 from closing on select
      $select.on("select2:selecting select2:unselecting", function(e) {
        // Allow the event but prevent closing
        setTimeout(() => {
          const select2Instance = $select.data("select2");
          if (select2Instance && typeof select2Instance.isOpen === 'function') {
            if (!select2Instance.isOpen()) {
              $select.select2("open");
            }
          }
        }, 1);
      });

      // Update checkboxes and display when selection changes
      $select.on("change", function (e) {
        const selectedValues = ($select.val() || []).map(v => String(v));
        
        // Filter out "__select_all__" for state
        const categoryCodes = selectedValues.filter(v => v !== '__select_all__');
        setWorkflowForm((prev) => ({ ...prev, catgCodes: categoryCodes }));
        updateSelectionDisplay();

        // Get all available category codes as strings for comparison
        const allCategoryCodes = categories.map(cat => {
          const code = cat.catgCode || cat.CatgCode || "";
          return String(code);
        }).filter(code => code);

        // Update checkboxes in dropdown immediately if it's open
        const updateCheckboxes = () => {
          const categoryCodesOnly = selectedValues.filter(v => v !== '__select_all__').map(v => String(v));
          
          $(".select2-results__option").each(function () {
            const $option = $(this);
            const optionData = $option.data("data");
            if (optionData && optionData.id) {
              const $checkbox = $option.find(".category-checkbox");
              if ($checkbox.length) {
                const optionId = String(optionData.id);
                if (optionId === '__select_all__') {
                  // "Select all" should be checked if all categories are selected
                  const shouldBeChecked = allCategoryCodes.length > 0 && 
                                        allCategoryCodes.every(code => categoryCodesOnly.includes(code));
                  $checkbox.prop("checked", shouldBeChecked);
                } else {
                  // Individual category checkbox
                  $checkbox.prop("checked", categoryCodesOnly.includes(optionId));
                }
              }
            }
          });
        };

        // Update immediately
        updateCheckboxes();

        // Also update after a delay to catch any re-renders
        setTimeout(updateCheckboxes, 100);
      });

      // Handle option click to toggle selection - scoped to category dropdown only
      $(document).on("click", ".select2-results__option", function (e) {
        const $option = $(this);
        const optionData = $option.data("data");

        // Only handle if this option has a category checkbox (to ensure it's our dropdown)
        if ($option.find(".category-checkbox").length === 0) {
          return;
        }

        // If clicking on checkbox, let the checkbox handler deal with it
        // But also handle it here as a fallback
        const isCheckboxClick = $(e.target).is(".category-checkbox") || 
                               $(e.target).closest(".category-checkbox").length > 0 ||
                               $(e.target).is("input[type='checkbox']");
        
        if (isCheckboxClick) {
          // Trigger the checkbox handler manually
          const $checkbox = $option.find(".category-checkbox");
          if ($checkbox.length) {
            console.log("Option click detected checkbox click, triggering handler");
            $checkbox.trigger("mousedown");
          }
          return;
        }

        if (optionData && optionData.id) {
          e.preventDefault();
          e.stopPropagation();

          const currentValues = ($select.val() || []).map(v => String(v));
          const categoryCodesOnly = currentValues.filter(v => v !== '__select_all__');
          const optionId = String(optionData.id);
          let isSelected = false;
          
          // Get all available category codes as strings
          const allCategoryCodes = categories.map(cat => {
            const code = cat.catgCode || cat.CatgCode || "";
            return String(code);
          }).filter(code => code);
          
          if (optionId === '__select_all__') {
            // "Select all" is checked if all categories are selected
            isSelected = allCategoryCodes.length > 0 && 
                        allCategoryCodes.every(code => categoryCodesOnly.includes(code));
          } else {
            isSelected = categoryCodesOnly.includes(optionId);
          }

          let newValues = [];
          
          if (optionId === '__select_all__') {
            // Handle "Select all" option
            if (isSelected) {
              // All are currently selected, so deselect all
              newValues = [];
            } else {
              // Not all are selected, so select ALL categories
              // Make sure we include all category codes
              if (allCategoryCodes.length > 0) {
                newValues = ['__select_all__', ...allCategoryCodes];
              } else {
                newValues = [];
              }
            }
          } else {
            // Handle individual category option
            if (isSelected) {
              // Deselect this option
              newValues = categoryCodesOnly.filter(v => v !== optionId);
            } else {
              // Select this option
              newValues = [...categoryCodesOnly, optionId];
              
              // Check if all categories are now selected
              if (allCategoryCodes.length > 0 && allCategoryCodes.every(code => newValues.includes(code))) {
                newValues = ['__select_all__', ...newValues];
              }
            }
          }

          // Update the checkbox immediately
          const $checkbox = $option.find(".category-checkbox");
          if ($checkbox.length) {
            const newCategoryCodesOnly = newValues.filter(v => v !== '__select_all__');
            if (optionId === '__select_all__') {
              const shouldBeChecked = allCategoryCodes.length > 0 && 
                                     allCategoryCodes.every(code => newCategoryCodesOnly.includes(code));
              $checkbox.prop("checked", shouldBeChecked);
            } else {
              $checkbox.prop("checked", !isSelected);
            }
          }

          // Update the selection
          $select.val(newValues).trigger("change");

          // Also update all checkboxes after a short delay to ensure consistency
          setTimeout(() => {
            const categoryCodesOnly = newValues.filter(v => v !== '__select_all__');
            
            $(".select2-results__option").each(function () {
              const $opt = $(this);
              const optData = $opt.data("data");
              if (optData && optData.id) {
                const $cb = $opt.find(".category-checkbox");
                if ($cb.length) {
                  if (optData.id === '__select_all__') {
                    const allCategoryCodes = categories.map(cat => {
                      const code = cat.catgCode || cat.CatgCode || "";
                      return code;
                    }).filter(code => code);
                    const shouldBeChecked = categoryCodesOnly.length === allCategoryCodes.length && allCategoryCodes.length > 0;
                    $cb.prop("checked", shouldBeChecked);
                  } else {
                    const shouldBeChecked = categoryCodesOnly.includes(
                      optData.id.toString()
                    );
                    $cb.prop("checked", shouldBeChecked);
                  }
                }
              }
            });
          }, 50);
        }
      });

      // Handle checkbox clicks directly - use mousedown to catch it before label click
      $(document).on("mousedown", ".category-checkbox", function(e) {
        e.stopPropagation(); // Prevent option click handler from firing
        e.preventDefault(); // Prevent default checkbox toggle - we control it manually
        
        const $checkbox = $(this);
        const optionId = String($checkbox.data("value") || "");
        
        console.log("Checkbox mousedown:", optionId, "Checkbox element:", $checkbox[0]);
        
        if (!optionId) {
          console.log("No optionId found, checkbox data:", $checkbox.data());
          return false;
        }
        
        // Get all available category codes from React state (same as templateResult uses)
        const allCategoryCodes = categories.map(cat => {
          const code = cat.catgCode || cat.CatgCode || "";
          return String(code);
        }).filter(code => code);
        
        // Get current state from Select2 values (source of truth)
        const currentValues = ($select.val() || []).map(v => String(v));
        const categoryCodesOnly = currentValues.filter(v => v !== '__select_all__');

        // Determine current selection state
        let isCurrentlySelected = false;
        if (optionId === '__select_all__') {
          isCurrentlySelected = allCategoryCodes.length > 0 && 
                               allCategoryCodes.every(code => categoryCodesOnly.includes(code));
        } else {
          isCurrentlySelected = categoryCodesOnly.includes(optionId);
        }

        // Toggle: if currently selected, deselect; if not selected, select
        let newValues = [];

        if (optionId === '__select_all__') {
          // Handle "Select all" checkbox
          console.log("Select all clicked. Currently selected:", isCurrentlySelected);
          console.log("All category codes:", allCategoryCodes);
          
          if (isCurrentlySelected) {
            // Currently all selected, so deselect all
            newValues = [];
            console.log("Deselecting all");
          } else {
            // Not all selected, so select ALL categories
            if (allCategoryCodes.length > 0) {
              newValues = ['__select_all__', ...allCategoryCodes];
              console.log("Selecting all:", newValues);
            } else {
              newValues = [];
              console.log("No categories available");
            }
          }
        } else {
          // Handle individual category checkbox
          if (isCurrentlySelected) {
            // Currently selected, so deselect
            newValues = categoryCodesOnly.filter(v => v !== optionId);
          } else {
            // Not selected, so select
            newValues = [...categoryCodesOnly, optionId];
            
            // Check if all categories are now selected
            if (allCategoryCodes.length > 0 && allCategoryCodes.every(code => newValues.includes(code))) {
              newValues = ['__select_all__', ...newValues];
            }
          }
        }

        // Update Select2 selection
        console.log("Setting new values:", newValues);
        $select.val(newValues);
        
        // Manually update all checkboxes immediately
        const newCategoryCodesOnly = newValues.filter(v => v !== '__select_all__');
        
        // Update all checkboxes in the dropdown
        $(".select2-results__option").each(function() {
          const $option = $(this);
          const $cb = $option.find(".category-checkbox");
          if ($cb.length) {
            const cbValue = String($cb.data("value") || "");
            if (cbValue === '__select_all__') {
              const shouldBeChecked = allCategoryCodes.length > 0 && 
                                    allCategoryCodes.every(code => newCategoryCodesOnly.includes(code));
              $cb.prop("checked", shouldBeChecked);
              console.log("Updated select all checkbox:", shouldBeChecked);
            } else {
              $cb.prop("checked", newCategoryCodesOnly.includes(cbValue));
            }
          }
        });
        
        // Trigger change event to update state and display
        $select.trigger("change");
        console.log("Change event triggered");
        
        // Return false to prevent any further event handling
        return false;
      });
      
      // Also handle click event as fallback
      $(document).on("click", ".category-checkbox", function(e) {
        // This will only fire if mousedown didn't prevent it
        e.stopPropagation();
        e.preventDefault();
      });

      // Update arrow direction and checkboxes when dropdown opens/closes
      $select.on("select2:open", function () {
        const $openArrow = $select
          .next(".select2-container")
          .find(".select2-selection__arrow b");
        if ($openArrow.length) {
          $openArrow.css({
            borderColor: "transparent transparent #888 transparent",
            borderWidth: "0 4px 5px 4px",
          });
        }

        // Attach handlers to checkboxes when dropdown opens (they're recreated by Select2)
        setTimeout(() => {
          // Remove any existing handlers first
          $(".category-checkbox").off("mousedown click");
          $(".category-option-with-checkbox").off("click");
          
          // Attach handler to all category checkboxes
          $(".category-checkbox").on("mousedown", function(e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            const $cb = $(this);
            const optId = String($cb.data("value") || "");
            
            console.log("Checkbox mousedown (after open):", optId);
            
            if (!optId) return false;
            
            // Get all available category codes from React state
            const allCatCodes = categories.map(cat => {
              const code = cat.catgCode || cat.CatgCode || "";
              return String(code);
            }).filter(code => code);
            
            // Get current state from Select2 values
            const currentVals = ($select.val() || []).map(v => String(v));
            const catCodesOnly = currentVals.filter(v => v !== '__select_all__');

            // Determine current selection state
            let isCurrSelected = false;
            if (optId === '__select_all__') {
              isCurrSelected = allCatCodes.length > 0 && 
                             allCatCodes.every(code => catCodesOnly.includes(code));
            } else {
              isCurrSelected = catCodesOnly.includes(optId);
            }

            // Toggle: if currently selected, deselect; if not selected, select
            let newVals = [];

            if (optId === '__select_all__') {
              if (isCurrSelected) {
                newVals = [];
                console.log("Deselecting all");
              } else {
                if (allCatCodes.length > 0) {
                  newVals = ['__select_all__', ...allCatCodes];
                  console.log("Selecting all:", newVals);
                } else {
                  newVals = [];
                }
              }
            } else {
              if (isCurrSelected) {
                newVals = catCodesOnly.filter(v => v !== optId);
              } else {
                newVals = [...catCodesOnly, optId];
                if (allCatCodes.length > 0 && allCatCodes.every(code => newVals.includes(code))) {
                  newVals = ['__select_all__', ...newVals];
                }
              }
            }

            // Update Select2 selection
            $select.val(newVals);
            
            // Manually update all checkboxes immediately
            const newCatCodesOnly = newVals.filter(v => v !== '__select_all__');
            
            // Update all checkboxes in the dropdown
            $(".select2-results__option").each(function() {
              const $opt = $(this);
              const $cb = $opt.find(".category-checkbox");
              if ($cb.length) {
                const cbVal = String($cb.data("value") || "");
                if (cbVal === '__select_all__') {
                  const shouldBeChecked = allCatCodes.length > 0 && 
                                        allCatCodes.every(code => newCatCodesOnly.includes(code));
                  $cb.prop("checked", shouldBeChecked);
                } else {
                  $cb.prop("checked", newCatCodesOnly.includes(cbVal));
                }
              }
            });
            
            // Trigger change event
            $select.trigger("change");
            
            return false;
          });
          
          // Also attach to labels
          $(".category-option-with-checkbox").on("click", function(e) {
            if ($(e.target).is(".category-checkbox")) {
              return; // Let checkbox handler handle it
            }
            
            e.preventDefault();
            e.stopPropagation();
            
            const $label = $(this);
            const $cb = $label.find(".category-checkbox");
            if ($cb.length) {
              $cb.trigger("mousedown");
            }
          });
          
          console.log("Handlers attached to", $(".category-checkbox").length, "checkboxes");
        }, 100);

        // Update checkboxes when dropdown opens
        // Add custom class to the .select2-container--default.select2-container--open element when category dropdown opens
        setTimeout(() => {
          const $container = $select.next(
            ".select2-container--default.select2-container--open"
          );
          if ($container.length) {
            $container.addClass("categoryDropdwn");

            // Find the dynamically added select2-results__options element (it's appended to body)
            // Wait a bit longer to ensure it's rendered, then find it and add class to parent
            setTimeout(() => {
              // Find the select2-results__options that's currently visible (for the category dropdown)
              const $resultsOptions = $(
                ".select2-results__options:visible"
              ).filter(function () {
                // Make sure it's the one for category dropdown by checking if it contains category checkboxes
                return $(this).find(".category-checkbox").length > 0;
              });

              if ($resultsOptions.length) {
                $resultsOptions.parent().addClass("select2-resultsOptnWrpr");
              }
            }, 100);
          }
        }, 50);

            setTimeout(() => {
              const selected = $select.val() || [];
              const categoryCodesOnly = selected.filter(v => v !== '__select_all__');

              $(".select2-results__option").each(function () {
            const $option = $(this);
            const optionData = $option.data("data");
            if (optionData && optionData.id) {
              const $checkbox = $option.find(".category-checkbox");
              if ($checkbox.length) {
                if (optionData.id === '__select_all__') {
                  // "Select all" should be checked if all categories are selected
                  const allCategoryCodes = categories.map(cat => {
                    const code = cat.catgCode || cat.CatgCode || "";
                    return code;
                  }).filter(code => code);
                  const shouldBeChecked = categoryCodesOnly.length === allCategoryCodes.length && allCategoryCodes.length > 0;
                  $checkbox.prop("checked", shouldBeChecked);
                } else {
                  $checkbox.prop(
                    "checked",
                    categoryCodesOnly.includes(optionData.id.toString())
                  );
                }
              }
            }
          });
        }, 50);
      });



      // Remove custom class when dropdown closes
      $select.on("select2:close", function () {
        const $container = $select.next(".select2-container--default");
        if ($container.length) {
          $container.removeClass("categoryDropdwn");

          // Remove class from the parent wrapper of dynamically added select2-results__options
          const $resultsOptions = $(".select2-results__options").filter(
            function () {
              return $(this).find(".category-checkbox").length > 0;
            }
          );
          if ($resultsOptions.length) {
            $resultsOptions.parent().removeClass("select2-resultsOptnWrpr");
          }
        }

        const $closeArrow = $select
          .next(".select2-container")
          .find(".select2-selection__arrow b");
        if ($closeArrow.length) {
          $closeArrow.css({
            borderColor: "#888 transparent transparent transparent",
            borderWidth: "5px 4px 0 4px",
          });
        }
        updateSelectionDisplay();
      });
      
      // Set initial values after Select2 is initialized
      const setInitialCategoryValues = () => {
        if (workflowForm.catgCodes && workflowForm.catgCodes.length > 0 && !isDisabled && categories.length > 0) {
          // Filter to only include valid category codes that exist in options
          const validCodes = workflowForm.catgCodes.filter(code => {
            return categories.some(cat => {
              const catCode = cat.catgCode || cat.CatgCode || cat.id || "";
              return catCode === code || catCode === code.toString();
            });
          });
          
          if (validCodes.length > 0) {
            $select.val(validCodes).trigger("change.select2");
            updateSelectionDisplay();
          } else {
            $select.val(null).trigger("change.select2");
            updateSelectionDisplay();
          }
        } else {
          $select.val(null).trigger("change.select2");
          updateSelectionDisplay();
        }
      };
      
      // Try multiple times to ensure Select2 is ready and categories are loaded
      setTimeout(setInitialCategoryValues, 100);
      setTimeout(setInitialCategoryValues, 500);
      setTimeout(setInitialCategoryValues, 1000);
    }, 200);
    
    // Cleanup
    return () => {
      clearTimeout(timer);
      $(document).off("mousedown", ".category-checkbox");
      $(document).off("click", ".category-checkbox");
      $(document).off("click", ".select2-results__option");
      if (catgSelectRef.current) {
        const $select = $(catgSelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:selecting select2:unselecting select2:open select2:close select2:change");
          $select.select2("destroy");
        }
      }
    };
  }, [categories, workflowForm.catgCodes, workflowForm.marketCode, showWorkflowForm, pageType]);

  // Initialize with one default row
  useEffect(() => {
    if (rows.length === 0) {
      const defaultRow = createDefaultRow();
      setRows([defaultRow]);
    }
  }, []);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Update rows with proper userRole values when userRoleOptions are loaded
  useEffect(() => {
    if (userRoleOptions.length > 0 && rows.length > 0 && editingWorkflow) {
      // Re-map rows to ensure userRole values match the option IDs
      const updatedRows = rows.map((row) => {
        if (row.userRole) {
          const userRoleCode = typeof row.userRole === 'string' 
            ? row.userRole 
            : (row.userRole?.id || row.userRole?.userTypeCode || row.userRole);
          
          if (userRoleCode && userRoleCode !== '' && userRoleCode !== '0000') {
            const matchingRole = userRoleOptions.find(
              role => {
                const roleId = role.id || role.userTypeCode;
                const codeStr = String(userRoleCode).trim();
                const roleIdStr = String(roleId).trim();
                return roleIdStr === codeStr || 
                       roleIdStr === codeStr.toUpperCase() ||
                       roleIdStr.toUpperCase() === codeStr.toUpperCase();
              }
            );
            if (matchingRole) {
              // Update with the correct ID format that Select2Dropdown expects
              const correctId = matchingRole.id || matchingRole.userTypeCode || userRoleCode;
              if (String(correctId) !== String(row.userRole)) {
                return { ...row, userRole: correctId };
              }
            }
          }
        }
        return row;
      });
      
      // Only update if there are actual changes
      const hasChanges = updatedRows.some((updatedRow, index) => {
        const originalRow = rows[index];
        return String(updatedRow.userRole || '') !== String(originalRow.userRole || '');
      });
      
      if (hasChanges) {
        setRows(updatedRows);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoleOptions, editingWorkflow]);

  // Load plants
  useEffect(() => {
    const loadPlants = async () => {
      try {
        const plantsRes = await masterDataAPI.getPlants();
        if (plantsRes?.success && plantsRes.data) {
          const plants = Array.isArray(plantsRes.data) ? plantsRes.data : [];
          setPlantOptions(
            plants.map((p) => ({
              id: p.plantCode || p.PlantCode || "",
              text: p.plantName || p.PlantName || "",
            }))
            .filter((p) => p.id && p.text)
          );
        }
      } catch (error) {
        console.error("Error loading plants:", error);
      }
    };
    loadPlants();
  }, []);

  // Workflow form functions
  const loadNextWorkflowCode = async () => {
    try {
      const response = await approvalWorkflowAPI.getNextCode();
      if (response?.success && response.data) {
        setWorkflowForm((prev) => ({ ...prev, workflowCode: response.data }));
      }
    } catch (error) {
      console.error("Error loading next workflow code:", error);
    }
  };

  const loadMarkets = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 500);
      if (response?.success) {
        // Check different possible response structures
        if (response.data?.data && Array.isArray(response.data.data)) {
          setMarkets(response.data.data);
        } else if (Array.isArray(response.data)) {
          setMarkets(response.data);
        } else if (response.data?.markets && Array.isArray(response.data.markets)) {
          setMarkets(response.data.markets);
        } else {
          setMarkets([]);
        }
      } else if (Array.isArray(response?.data)) {
        setMarkets(response.data);
      } else {
        setMarkets([]);
      }
    } catch (e) {
      console.error("Error loading markets:", e);
      setMarkets([]);
    }
  };

  // Add mode: pre-select one market (active first, else first row) — same rule as Add User
  useEffect(() => {
    if (!isAddMode || !showWorkflowForm || !markets.length) return;

    const pickDefaultMarketCode = (marketList) => {
      const active =
        marketList.find((m) => (m.isActive ?? m.ISACTIVE ?? m.IsActive) === true) ||
        marketList.find((m) => (m.isActive ?? m.ISACTIVE ?? m.IsActive) === 1);
      const m = active || marketList[0];
      if (!m) return "";
      return String(m.marketCode || m.MARKETCODE || m.MarketCode || "").trim();
    };

    const code = pickDefaultMarketCode(markets);
    if (!code) return;

    if (pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration") {
      setWorkflowForm((prev) => {
        if (prev.marketCodes?.length) return prev;
        return { ...prev, marketCodes: [code], marketCode: code };
      });
    } else {
      setWorkflowForm((prev) => {
        if (prev.marketCode && prev.marketCode !== "0000") return prev;
        return { ...prev, marketCode: code };
      });
    }
  }, [markets, isAddMode, pageType, showWorkflowForm]);

  const loadCategories = async (marketCode) => {
    try {
      if (!marketCode || marketCode === "0000") {
        setCategories([]);
        return;
      }
      
      const response = await productMasterAPI.getCategories(true, marketCode);
      if (response?.success && response.data) {
        setCategories(Array.isArray(response.data) ? response.data : []);
      } else if (response?.data) {
        setCategories(Array.isArray(response.data) ? response.data : []);
      } else {
        setCategories([]);
      }
    } catch (e) {
      console.error("Error loading categories:", e);
      setCategories([]);
    }
  };

  const loadWorkflowForEdit = async () => {
    try {
      const response = await approvalWorkflowAPI.get(workflowCode);
      if (response?.success && response.data) {
        const workflow = response.data;
        setEditingWorkflow(workflow);
        const marketCodeRaw = workflow.marketCode || workflow.MARKETCODE || "";
        const catgCode = workflow.catgCode || workflow.CATGCODE || "";
        const srTypeCode = workflow.srTypeCode || workflow.SRTYPECODE || "";

        // Get pageType from workflow data or URL params or default
        const workflowPageType = workflow.pageType || workflow.PageType || searchParams.get('pageType') || 'ApprovalConfiguration';

        const marketCodesForForm =
          (workflowPageType === "ApprovalConfiguration" || workflowPageType === "SLATatConfiguration") &&
          marketCodeRaw
            ? String(marketCodeRaw)
                .split(",")
                .map((s) => s.trim())
                .filter((c) => c && c !== "0000")
            : marketCodeRaw && marketCodeRaw !== "0000"
              ? [String(marketCodeRaw).trim()]
              : [];
        const marketCodeSingle =
          workflowPageType === "ApprovalConfiguration" || workflowPageType === "SLATatConfiguration"
            ? marketCodesForForm[0] || "0000"
            : marketCodeRaw || "0000";

        const catgCodesArray =
          catgCode && catgCode !== "0000" && catgCode !== "" && catgCode !== null
            ? catgCode.split(",").map((c) => c.trim()).filter((c) => c)
            : [];

        let srTypeCodeString =
          srTypeCode && srTypeCode !== "0000" && srTypeCode !== "" && srTypeCode !== null
            ? String(srTypeCode).trim()
            : "";
        if (
          (workflowPageType === "ApprovalConfiguration" ||
            workflowPageType === "SLATatConfiguration") &&
          srTypeCodeString &&
          srTypeCodeString.includes(",")
        ) {
          srTypeCodeString = srTypeCodeString.split(",")[0].trim();
        }

        const wfSourceRaw =
          workflow.workflowSource || workflow.WorkflowSource || workflow.WORKFLOWSOURCE || "";
        const userSpecFromSource =
          workflowPageType === "ApprovalConfiguration" || workflowPageType === "SLATatConfiguration"
            ? parseUserSpecificationFromWorkflowSource(wfSourceRaw)
            : "None";

        // Update pageType state
        setPageType(workflowPageType);
        
        // Ensure user roles are loaded before loading levels
        if (userRoleOptions.length === 0) {
          await loadUserRoles();
        }
        
        // Load options before setting form (multi-market flows: SR types + BV loads via effect)
        if (workflowPageType === "ApprovalConfiguration" || workflowPageType === "SLATatConfiguration") {
          if (marketCodeSingle && marketCodeSingle !== "0000") {
            await loadSRTypes(marketCodeSingle);
          }
          await new Promise((resolve) => setTimeout(resolve, 150));
        } else if (marketCodeSingle && marketCodeSingle !== "0000") {
          await loadCategories(marketCodeSingle);
          await loadSRTypes(marketCodeSingle);
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Now set workflowForm after SR Types are loaded
        setWorkflowForm({
          workflowCode: workflow.workflowCode || workflow.WorkflowCode || "",
          workflowName: workflow.workflowName || workflow.WorkflowName || "",
          srTypeCode: srTypeCodeString,
          marketCode: marketCodeSingle || "0000",
          marketCodes:
            workflowPageType === "ApprovalConfiguration" ||
            workflowPageType === "SLATatConfiguration"
              ? marketCodesForForm.length
                ? [marketCodesForForm[0]]
                : []
              : [],
          catgCodes: catgCodesArray,
          pageType: workflowPageType,
          userSpecificationType: userSpecFromSource,
          workflowSource: wfSourceRaw,
          slaDays: workflow.slaDays != null ? workflow.slaDays : workflow.SlaDays != null ? workflow.SlaDays : workflow.SLADAYS != null ? workflow.SLADAYS : "",
          tatDays: workflow.tatDays != null ? workflow.tatDays : workflow.TatDays != null ? workflow.TatDays : workflow.TATDAYS != null ? workflow.TATDAYS : "",
          isActive: workflow.isActive !== undefined ? workflow.isActive : workflow.IsActive !== undefined ? workflow.IsActive : true,
        });
        
        // Load levels for this workflow (after user roles are loaded)
        await loadWorkflowLevels(workflowCode);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load workflow details",
        });
        navigate(searchParams.get("pageType") === "SLATatConfiguration" ? "/sla-tat-configuration" : "/dynamic-approval-configuration");
      }
    } catch (error) {
      console.error("Error loading workflow:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load workflow details",
      });
      navigate(searchParams.get("pageType") === "SLATatConfiguration" ? "/sla-tat-configuration" : "/dynamic-approval-configuration");
    }
  };

  const loadWorkflowLevels = async (workflowCode) => {
    try {
      const response = await approvalWorkflowAPI.getLevels(workflowCode);
      if (response?.success && response.data) {
        const levels = Array.isArray(response.data) ? response.data : [];
        
        // Wait a bit to ensure userRoleOptions are loaded, then map rows
        const mapRowsWithRoles = () => {
          const mappedRows = levels.map((level, index) => {
            const userRoleCode = level.usertypecode || level.userTypeCode || level.USERTYPECODE || null;
            
            // Find the matching user role object if code exists and userRoleOptions are loaded
            let userRoleValue = userRoleCode;
            if (userRoleCode && userRoleOptions.length > 0) {
              const matchingRole = userRoleOptions.find(
                role => {
                  const roleId = role.id || role.userTypeCode;
                  const codeStr = String(userRoleCode).trim();
                  const roleIdStr = String(roleId).trim();
                  return roleIdStr === codeStr || 
                         roleIdStr === codeStr.toUpperCase() ||
                         roleIdStr.toUpperCase() === codeStr.toUpperCase();
                }
              );
              if (matchingRole) {
                // Use the id from the matching role to ensure it matches Select2Dropdown's getOptionValue
                userRoleValue = matchingRole.id || userRoleCode;
              }
            }
            
            return {
              id: `row-${Date.now()}-${index}`,
              approvalLevelCode: level.approvalLevelCode || level.ApprovalLevelCode || "",
              levelSequence: level.levelSequence || level.LevelSequence || index + 1,
              approvalType: level.approvalType || level.ApprovalType || "Required",
              userRole: userRoleValue, // Use the code string that matches the option id
              minValue: level.minValue || level.MinValue || "",
              maxValue: level.maxValue || level.MaxValue || "",
              currency: level.currency || level.Currency || "INR",
              plantCode: level.plantcode || level.plantCode || level.PLANTCODE || "",
              isPlantQCRequired: level.isPlantQCRequired !== undefined 
                ? level.isPlantQCRequired 
                : level.IsPlantQCRequired !== undefined 
                ? level.IsPlantQCRequired 
                : false,
              slaDays: level.slaDays != null ? level.slaDays : level.SlaDays != null ? level.SlaDays : "",
              tatDays: level.tatDays != null ? level.tatDays : level.TatDays != null ? level.TatDays : "",
              isActive: level.isActive !== undefined 
                ? level.isActive 
                : level.IsActive !== undefined 
                ? level.IsActive 
                : true,
            };
          });
          setRows(mappedRows.length > 0 ? mappedRows : [createDefaultRow()]);
        };
        
        // Always map rows - if userRoleOptions aren't loaded yet, 
        // the useEffect hook will update them when roles become available
        mapRowsWithRoles();
      }
    } catch (error) {
      console.error("Error loading workflow levels:", error);
    }
  };

  const handleSaveWorkflow = async () => {
    // Validation
    if (!workflowForm.workflowName.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Workflow Name is required",
      });
      return;
    }

    // In add mode, workflowCode is auto-generated, so try to load it if not present
    // If it fails to load, proceed anyway as backend can generate it
    if (!editingWorkflow && !workflowForm.workflowCode.trim()) {
      try {
        const response = await approvalWorkflowAPI.getNextCode();
        if (response?.success && response.data) {
          setWorkflowForm((prev) => ({ ...prev, workflowCode: response.data }));
        }
      } catch (error) {
        console.warn("Error loading next workflow code, proceeding with save:", error);
        // Continue with save - backend will generate the code if needed
      }
    }

    try {
      // Join categories array with commas for database storage
      const catgCodeString = workflowForm.catgCodes && workflowForm.catgCodes.length > 0 
        ? workflowForm.catgCodes.join(',') 
        : null;

      let srTypeCodeString = null;
      if (workflowForm.srTypeCode) {
        if (Array.isArray(workflowForm.srTypeCode)) {
          srTypeCodeString =
            workflowForm.srTypeCode.length > 0
              ? workflowForm.srTypeCode.join(",")
              : null;
        } else if (typeof workflowForm.srTypeCode === "string" && workflowForm.srTypeCode.trim() !== "") {
          srTypeCodeString = workflowForm.srTypeCode.trim();
        }
      }
      if (
        ((workflowForm.pageType || pageType) === "ApprovalConfiguration" ||
          (workflowForm.pageType || pageType) === "SLATatConfiguration") &&
        srTypeCodeString &&
        srTypeCodeString.includes(",")
      ) {
        srTypeCodeString = srTypeCodeString.split(",")[0].trim();
      }

      const wfPage = workflowForm.pageType || pageType;
      const marketForApi =
        wfPage === "ApprovalConfiguration" || wfPage === "SLATatConfiguration"
          ? workflowForm.marketCodes?.length
            ? [...workflowForm.marketCodes].filter(Boolean).join(",")
            : null
          : workflowForm.marketCode && workflowForm.marketCode !== "0000"
            ? workflowForm.marketCode
            : null;

      const workflowData = {
        workflowCode: workflowForm.workflowCode,
        workflowName: workflowForm.workflowName,
        SRTYPECODE: srTypeCodeString,
        MARKETCODE: marketForApi,
        CATGCODE: catgCodeString,
        pageType: workflowForm.pageType || pageType, // Add PageType field
        isActive: workflowForm.isActive,
      };
      if ((workflowForm.pageType || pageType) === "ApprovalConfiguration" || (workflowForm.pageType || pageType) === "SLATatConfiguration") {
        workflowData.workflowSource = JSON.stringify({
          userSpecificationType: workflowForm.userSpecificationType || "None",
        });
      }

      let response;
      if (editingWorkflow) {
        response = await approvalWorkflowAPI.update(workflowForm.workflowCode, workflowData);
      } else {
        response = await approvalWorkflowAPI.create(workflowData);
      }

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: editingWorkflow ? "Workflow updated successfully" : "Workflow created successfully",
        });
        setShowWorkflowForm(false);
        setEditingWorkflow(null);
        // Don't reset form if we're in edit mode - keep the data
        if (!isEditMode) {
          lastSrTypeAutoFillKeyRef.current = "";
          setWorkflowForm({
            workflowCode: "",
            workflowName: "",
            srTypeCode: "",
            marketCode: "0000",
            marketCodes: [],
            catgCodes: [],
            pageType: pageType || initialPageType,
            userSpecificationType: "None",
            workflowSource: "",
            slaDays: "",
            tatDays: "",
            isActive: true,
          });
          setCategories([]);
          setBusinessVerticals([]);
        }
        if (isAddMode || isEditMode) {
          // Stay on the same page after save
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to save workflow",
        });
      }
    } catch (error) {
      console.error("Error saving workflow:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save workflow",
      });
    }
  };

  /**
   * Create a default row with empty/default values
   */
  const createDefaultRow = () => {
    return {
      id: `row-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      approvalLevelCode: "",
      levelSequence: 1,
      approvalType: "Required",
      userRole: null,
      minValue: "",
      maxValue: "",
      currency: "INR",
      plantCode: "",
      isPlantQCRequired: false,
      slaDays: "",
      tatDays: "",
      isActive: true,
    };
  };

  /**
   * Load user roles from API or use mock data
   */
  const loadUserRoles = async () => {
    try {
      // Try to load from API
      try {
        const response = await masterDataAPI.getUserTypes();
        if (response?.success && response.data) {
          setUserRoleOptions(
            response.data.map((role) => ({
              id: role.userTypeCode || role.UserTypeCode,
              name: role.userTypeName || role.UserTypeName,
            }))
          );
        } else {
          // Fallback to mock data
          setUserRoleOptions(getMockUserRoles());
        }
      } catch (error) {
        console.warn("Failed to load user roles from API, using mock data:", error);
        setUserRoleOptions(getMockUserRoles());
      }
    } catch (error) {
      console.error("Error loading user roles:", error);
      setUserRoleOptions(getMockUserRoles());
    }
  };

  /**
   * Mock user roles data (reusable and configurable)
   */
  const getMockUserRoles = () => {
    return [
      { id: "ADMIN", name: "Administrator" },
      { id: "APPROVER", name: "Approver" },
      { id: "SE", name: "Sales Executive" },
      { id: "PLANT_QC", name: "Plant QC" },
      { id: "MANAGER", name: "Manager" },
      { id: "SUPERVISOR", name: "Supervisor" },
    ];
  };

  /**
   * Load all user-role codes from opposite pageType workflows once (cached; levels fetched in parallel).
   */
  const getOppositePageRoleToWorkflowMap = async () => {
    const now = Date.now();
    const cache = oppositePageRolesCacheRef.current;
    if (
      cache.pageType === pageType &&
      cache.roleToWorkflow &&
      now - cache.at < 5 * 60 * 1000
    ) {
      return cache.roleToWorkflow;
    }

    if (oppositePageRolesLoadPromiseRef.current) {
      return oppositePageRolesLoadPromiseRef.current;
    }

    const loadPromise = (async () => {
      const oppositePageType =
        pageType === "ApprovalConfiguration"
          ? "ApprovalValueConfiguration"
          : "ApprovalConfiguration";

      const roleToWorkflow = new Map();

      try {
        const workflowsResponse = await approvalWorkflowAPI.getAll({
          isActive: true,
        });
        if (!workflowsResponse?.success || !workflowsResponse.data) {
          return roleToWorkflow;
        }

        const workflows = Array.isArray(workflowsResponse.data)
          ? workflowsResponse.data
          : workflowsResponse.data.workflows || [];

        const oppositeWorkflows = workflows.filter(
          (w) => (w.pageType || w.PageType) === oppositePageType
        );

        await Promise.all(
          oppositeWorkflows.map(async (workflow) => {
            const workflowCode =
              workflow.workflowCode || workflow.WorkflowCode;
            if (!workflowCode) return;

            try {
              const levelsResponse =
                await approvalWorkflowAPI.getLevels(workflowCode);
              if (!levelsResponse?.success || !levelsResponse.data) return;

              const levels = Array.isArray(levelsResponse.data)
                ? levelsResponse.data
                : levelsResponse.data.levels || [];

              const workflowName =
                workflow.workflowName ||
                workflow.WorkflowName ||
                workflowCode;

              levels.forEach((level) => {
                const levelRoleCode =
                  level.userTypeCode ||
                  level.UserTypeCode ||
                  level.userRole ||
                  level.UserRole ||
                  level.roleCode ||
                  level.RoleCode;
                const code = String(levelRoleCode ?? "").trim();
                if (code && code !== "0000" && !roleToWorkflow.has(code)) {
                  roleToWorkflow.set(code, workflowName);
                }
              });
            } catch (error) {
              console.warn(
                `Error checking levels for workflow ${workflowCode}:`,
                error
              );
            }
          })
        );
      } catch (error) {
        console.error("Error loading opposite pageType roles:", error);
      }

      oppositePageRolesCacheRef.current = {
        pageType,
        at: Date.now(),
        roleToWorkflow,
      };
      return roleToWorkflow;
    })();

    oppositePageRolesLoadPromiseRef.current = loadPromise;
    try {
      return await loadPromise;
    } finally {
      oppositePageRolesLoadPromiseRef.current = null;
    }
  };

  /**
   * Check if a role already exists in workflows of the opposite pageType
   */
  const checkRoleExistsInOppositePageType = async (roleCode) => {
    if (!roleCode || roleCode === "" || roleCode === "0000") {
      return { exists: false, workflowName: null };
    }

    try {
      const roleToWorkflow = await getOppositePageRoleToWorkflowMap();
      const key = String(roleCode).trim();
      if (roleToWorkflow.has(key)) {
        return {
          exists: true,
          workflowName: roleToWorkflow.get(key),
        };
      }
      return { exists: false, workflowName: null };
    } catch (error) {
      console.error("Error checking role in opposite pageType:", error);
      return { exists: false, workflowName: null };
    }
  };

  /**
   * Validate min/max values for a specific row
   */
  const validateRowRange = (rowId, minValue, maxValue, allRows) => {
    const currentRow = allRows.find(r => r.id === rowId);
    if (!currentRow) return { isValid: true, error: null };
    
    // Skip min/max range validation for Approval and SLA/TAT level configuration
    if (pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration") {
      return { isValid: true, error: null };
    }
    
    const rowIndex = allRows.findIndex(r => r.id === rowId);
    const errors = [];
    
    // Check if both values are provided
    if (minValue && maxValue) {
      const minValueStr = String(minValue).trim();
      const maxValueStr = String(maxValue).trim();
      
      // Validate integers
      if (!/^-?\d+$/.test(minValueStr)) {
        return { isValid: false, error: "Min Value must be an integer" };
      }
      
      if (!/^-?\d+$/.test(maxValueStr)) {
        return { isValid: false, error: "Max Value must be an integer" };
      }
      
      const min = parseInt(minValueStr, 10);
      const max = parseInt(maxValueStr, 10);
      
      // Check max > min
      if (min >= max) {
        return { isValid: false, error: "Max Value must be greater than Min Value" };
      }
      
      // Check for overlaps with other rows
      const otherRows = allRows.filter((r, idx) => idx !== rowIndex && r.minValue && r.maxValue);
      for (const otherRow of otherRows) {
        const otherMinStr = String(otherRow.minValue).trim();
        const otherMaxStr = String(otherRow.maxValue).trim();
        
        if (/^-?\d+$/.test(otherMinStr) && /^-?\d+$/.test(otherMaxStr)) {
          const otherMin = parseInt(otherMinStr, 10);
          const otherMax = parseInt(otherMaxStr, 10);
          
          // Check for overlap
          if ((min <= otherMax && max >= otherMin)) {
            const otherIndex = allRows.findIndex(r => r.id === otherRow.id);
            return { 
              isValid: false, 
              error: `Overlaps with Row ${otherIndex + 1} (${otherMin}-${otherMax})` 
            };
          }
        }
      }
      
      // Check sequential ordering (next row's min should be > current max)
      if (rowIndex < allRows.length - 1) {
        const nextRow = allRows[rowIndex + 1];
        if (nextRow.minValue) {
          const nextMinStr = String(nextRow.minValue).trim();
          if (/^-?\d+$/.test(nextMinStr)) {
            const nextMin = parseInt(nextMinStr, 10);
            if (max >= nextMin) {
              return { 
                isValid: false, 
                error: `Next row's Min (${nextMin}) must be > Max (${max})` 
              };
            }
          }
        }
      }
      
      // Check previous row's max should be < current min
      if (rowIndex > 0) {
        const prevRow = allRows[rowIndex - 1];
        if (prevRow.maxValue) {
          const prevMaxStr = String(prevRow.maxValue).trim();
          if (/^-?\d+$/.test(prevMaxStr)) {
            const prevMax = parseInt(prevMaxStr, 10);
            if (min <= prevMax) {
              return { 
                isValid: false, 
                error: `Min (${min}) must be > previous row's Max (${prevMax})` 
              };
            }
          }
        }
      }
    } else if (minValue || maxValue) {
      // Only one value provided
      return { isValid: false, error: "Both Min and Max values are required" };
    }
    
    return { isValid: true, error: null };
  };

  /**
   * Handle field change for a specific row
   */
  const handleRowChange = async (rowId, field, value) => {
    if (field === "userRole") {
      const previousRole =
        rowsRef.current.find((r) => r.id === rowId)?.userRole ?? null;

      // Apply selection immediately so + / save see the role before async checks finish
      setRows((prevRows) => {
        const updatedRows = prevRows.map((row) =>
          row.id === rowId ? { ...row, userRole: value ?? null } : row
        );
        rowsRef.current = updatedRows;
        return updatedRows;
      });

      if (!value) return;

      const roleCode = resolveUserRoleCode(value, userRoleOptions);
      if (!roleCode) return;

      if (pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration") {
        const duplicateRow = rowsRef.current.find((row) => {
          if (row.id === rowId || !row.userRole) return false;
          const existingRoleCode = resolveUserRoleCode(
            row.userRole,
            userRoleOptions
          );
          return (
            existingRoleCode &&
            String(existingRoleCode).trim() === String(roleCode).trim()
          );
        });

        if (duplicateRow) {
          setRows((prevRows) => {
            const updatedRows = prevRows.map((row) =>
              row.id === rowId ? { ...row, userRole: previousRole } : row
            );
            rowsRef.current = updatedRows;
            return updatedRows;
          });
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "This role is already selected in another row. Please select a different role or remove it from the other row first.",
          });
          return;
        }
      }

      setValidatingRoleRowId(rowId);
      try {
        const checkResult = await checkRoleExistsInOppositePageType(roleCode);
        if (checkResult.exists) {
          const oppositePageType =
            pageType === "ApprovalConfiguration"
              ? "Approval Value Configuration"
              : "Approval Configuration";

          setRows((prevRows) => {
            const updatedRows = prevRows.map((row) =>
              row.id === rowId ? { ...row, userRole: previousRole } : row
            );
            rowsRef.current = updatedRows;
            return updatedRows;
          });
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `This role already exists in ${oppositePageType} (Workflow: ${checkResult.workflowName}). The same role cannot be used in both page types.`,
          });
          return;
        }
      } finally {
        setValidatingRoleRowId(null);
      }
      return;
    }

    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      );
      
      // Real-time validation for min/max values
      if (field === 'minValue' || field === 'maxValue') {
        const updatedRow = updatedRows.find(r => r.id === rowId);
        if (updatedRow) {
          const validation = validateRowRange(
            rowId, 
            updatedRow.minValue, 
            updatedRow.maxValue, 
            updatedRows
          );
          // Store validation error in row state
          updatedRow.rangeError = validation.error;
          updatedRow.rangeValid = validation.isValid;
        }
      }
      
      return updatedRows;
    });
  };

  /**
   * Insert a new row below currentIndex (instant — role/opposite-page checks run on role select and save).
   */
  const handleInsertRow = (currentIndex) => {
    if (insertingRowAfterIndex !== null) return;

    const currentRow = rowsRef.current[currentIndex] ?? rows[currentIndex];
    if (!currentRow) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: `Row ${currentIndex + 1} not found.`,
      });
      return;
    }

    const roleCode = resolveUserRoleCode(currentRow.userRole, userRoleOptions);
    if (!roleCode) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: `Please select a User Role for row ${currentIndex + 1} before adding a new row.`,
      });
      return;
    }

    setInsertingRowAfterIndex(currentIndex);
    try {
      const newRow = createDefaultRow();
      setRows((prevRows) => {
        const newRows = [...prevRows];
        newRows.splice(currentIndex + 1, 0, newRow);
        const updatedRows = newRows.map((row, idx) => ({
          ...row,
          levelSequence: idx + 1,
        }));
        const validatedRows = validateAllRows(updatedRows);
        rowsRef.current = validatedRows;
        return validatedRows;
      });
    } finally {
      setInsertingRowAfterIndex(null);
    }
  };

  /**
   * Delete a row by ID
   */
  const handleDeleteRow = (rowId) => {
    if (rows.length <= 1) {
      // Prevent deleting the last row
      return;
    }
    setRows((prevRows) => {
      const filtered = prevRows.filter((row) => row.id !== rowId);
      // Update level sequences after deletion
      const updatedRows = filtered.map((row, idx) => ({
        ...row,
        levelSequence: idx + 1
      }));
      
      // Validate all rows after deletion to check sequential ordering
      const validatedRows = validateAllRows(updatedRows);
      
      return validatedRows;
    });
    
    // Clean up Select2 instance if it exists
    if (select2Refs.current[rowId]) {
      const $select = $(select2Refs.current[rowId]);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      delete select2Refs.current[rowId];
    }
  };

  /**
   * Drag and Drop Handlers
   */
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
    e.target.style.opacity = "0.5";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  /**
   * Validate all rows after reordering
   */
  const validateAllRows = (allRows) => {
    return allRows.map((row) => {
      const validation = validateRowRange(
        row.id,
        row.minValue,
        row.maxValue,
        allRows
      );
      return {
        ...row,
        rangeError: validation.error,
        rangeValid: validation.isValid
      };
    });
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setRows((prevRows) => {
      const newRows = [...prevRows];
      const draggedRow = newRows[draggedIndex];
      
      // Remove dragged item
      newRows.splice(draggedIndex, 1);
      
      // Insert at new position
      const newIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      newRows.splice(newIndex, 0, draggedRow);
      
      // Update level sequences based on new positions
      const reorderedRows = newRows.map((row, idx) => ({
        ...row,
        levelSequence: idx + 1
      }));
      
      // Validate all rows after reordering to check sequential ordering
      const validatedRows = validateAllRows(reorderedRows);
      
      return validatedRows;
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  /**
   * Load options for Select2 dropdown (async)
   * @param {string} searchText - Search text to filter roles
   * @param {number} pageNumber - Page number for pagination
   * @param {number} pageSize - Number of items per page
   * @param {string} currentRowId - ID of the current row (to exclude its selected role from filtering)
   */
  const loadRoleOptions = async (searchText, pageNumber, pageSize, currentRowId = null) => {
    // Exclude roles picked in other rows only when duplicates are disallowed (not Level Configuration)
    const selectedRoleIds =
      pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration"
        ? []
        : rows
            .filter(row => row.id !== currentRowId && row.userRole)
            .map(row => {
              const roleValue = row.userRole;
              if (typeof roleValue === 'string') {
                return roleValue.trim();
              } else if (typeof roleValue === 'object' && roleValue !== null) {
                return roleValue.id || roleValue.userTypeCode || String(roleValue);
              } else if (typeof roleValue === 'number') {
                return String(roleValue);
              }
              return null;
            })
            .filter(id => id && id !== '' && id !== '0000');

    const filtered = userRoleOptions.filter((role) => {
      const roleId = String(role.id || role.userTypeCode || '');
      const matchesSearch = role.name.toLowerCase().includes(searchText.toLowerCase());
      const notSelected = !selectedRoleIds.includes(roleId);
      return matchesSearch && notSelected;
    });

    // Simulate pagination
    const start = (pageNumber - 1) * pageSize;
    const end = start + pageSize;
    const paginated = filtered.slice(start, end);

    return {
      data: paginated,
      totalCount: filtered.length,
    };
  };

  /**
   * Handle combined save action - saves both workflow and levels
   */
  const handleSave = async () => {
    if (saving) return;

    if (!workflowForm.workflowName.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Workflow Name is required",
      });
      return;
    }

    // In add mode, workflowCode is auto-generated, so ensure it's loaded before saving
    try {
    let finalWorkflowCode = workflowForm.workflowCode;
    if (!editingWorkflow && !finalWorkflowCode.trim()) {
      try {
        const response = await approvalWorkflowAPI.getNextCode();
        if (response?.success && response.data) {
          finalWorkflowCode = response.data;
          setWorkflowForm((prev) => ({ ...prev, workflowCode: finalWorkflowCode }));
        } else {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Could not generate workflow code. Please try again.",
          });
          return;
        }
      } catch (error) {
        console.error("Error loading next workflow code:", error);
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Could not generate workflow code. Please try again.",
        });
        return;
      }
    }

    // Ensure workflowCode is not empty before proceeding
    if (!finalWorkflowCode || !finalWorkflowCode.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Workflow Code is required",
      });
      return;
    }

    if (pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration") {
      if (!Array.isArray(workflowForm.marketCodes) || workflowForm.marketCodes.length === 0) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Market is required",
        });
        return;
      }
    } else if (!workflowForm.marketCode || workflowForm.marketCode === "0000") {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Market is required",
      });
      return;
    }

    if (!workflowForm.catgCodes || workflowForm.catgCodes.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text:
          pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration"
            ? "At least one Business Vertical is required"
            : "At least one Product Category is required",
      });
      return;
    }

    if (pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration") {
      const spec = String(workflowForm.userSpecificationType || "").trim();
      const allowed = getUserSpecificationOptionsForMarkets(workflowForm.marketCodes, markets);
      if (!spec || spec === "None" || !allowed.some((o) => o.id === spec)) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "SR Journey Flow is required",
        });
        return;
      }
    }

    // Validate rows
    if (rows.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "At least one approval level is required",
      });
      return;
    }

    // Validate each row
    const rowRanges = []; // Store valid ranges for overlap checking

    // Opposite page-type role check runs on User Role change (cached). Skip on save for level-config pages.
    const skipOppositePageCheckOnSave =
      pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration";
    let oppositeRoleMap = null;
    if (!skipOppositePageCheckOnSave) {
      oppositeRoleMap = await getOppositePageRoleToWorkflowMap();
    }
    const oppositePageLabel =
      pageType === "ApprovalConfiguration"
        ? "Approval Value Configuration"
        : "Approval Configuration";
    
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      
      const roleCode = resolveUserRoleCode(row.userRole, userRoleOptions);
      
      if (!roleCode) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: `Please select a User Role for row ${i + 1} before saving.`,
        });
        return;
      }

      if (!skipOppositePageCheckOnSave && oppositeRoleMap?.has(roleCode)) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: `Row ${i + 1}: This role already exists in ${oppositePageLabel} (Workflow: ${oppositeRoleMap.get(roleCode)}). The same role cannot be used in both page types.`,
        });
        return;
      }
      
      // Validate MinValue and MaxValue range - Skip for ApprovalConfiguration and SLATatConfiguration
      if (pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration") {
        if (row.minValue && row.maxValue) {
          // Check if values are valid integers
          const minValueStr = String(row.minValue).trim();
          const maxValueStr = String(row.maxValue).trim();
          
          // Validate that values are integers (not decimals)
          if (!/^-?\d+$/.test(minValueStr)) {
            Swal.fire({
              icon: "error",
              title: "Validation Error",
              text: `In row ${i + 1}: Min Value must be an integer (whole number). Current value: ${minValueStr}`,
            });
            return;
          }
          
          if (!/^-?\d+$/.test(maxValueStr)) {
            Swal.fire({
              icon: "error",
              title: "Validation Error",
              text: `In row ${i + 1}: Max Value must be an integer (whole number). Current value: ${maxValueStr}`,
            });
            return;
          }
          
          const minValue = parseInt(minValueStr, 10);
          const maxValue = parseInt(maxValueStr, 10);
          
          if (!isNaN(minValue) && !isNaN(maxValue)) {
            // First validation: Max value must be greater than Min value
            if (minValue >= maxValue) {
              Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: `In row ${i + 1}: Max Value must be greater than Min Value. Current values: Min=${minValue}, Max=${maxValue}`,
              });
              return;
            }
            
            // Store this range for overlap checking
            rowRanges.push({
              rowIndex: i,
              min: minValue,
              max: maxValue,
            });
          }
        } else if (row.minValue || row.maxValue) {
          // If only one value is provided, show error
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `In row ${i + 1}: Both Min Value and Max Value are required for range validation.`,
          });
          return;
        }
      }
    }
    
    // Validate range overlaps and sequential ordering - Skip for ApprovalConfiguration and SLATatConfiguration
    if (pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration" && rowRanges.length > 1) {
      // Check ranges in row order (not sorted) to ensure sequential progression
      // Sort by row index to maintain row order
      const orderedRanges = [...rowRanges].sort((a, b) => a.rowIndex - b.rowIndex);
      
      // Check for overlaps and ensure sequential ordering
      for (let i = 0; i < orderedRanges.length - 1; i++) {
        const currentRange = orderedRanges[i];
        const nextRange = orderedRanges[i + 1];
        
        // Check if ranges overlap (current max should be < next min for sequential ranges)
        if (currentRange.max >= nextRange.min) {
          const currentRowIndex = currentRange.rowIndex + 1;
          const nextRowIndex = nextRange.rowIndex + 1;
          
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Range overlap detected! Row ${currentRowIndex} (${currentRange.min}-${currentRange.max}) overlaps with Row ${nextRowIndex} (${nextRange.min}-${nextRange.max}). Ranges must be sequential and non-overlapping. The next row's Min Value should be greater than the previous row's Max Value.`,
          });
          return;
        }
      }
      
      // Also check all pairs for any overlaps (in case rows are not in order)
      for (let i = 0; i < rowRanges.length; i++) {
        for (let j = i + 1; j < rowRanges.length; j++) {
          const range1 = rowRanges[i];
          const range2 = rowRanges[j];
          
          // Check if ranges overlap (not just sequential)
          if (
            (range1.min <= range2.max && range1.max >= range2.min) ||
            (range2.min <= range1.max && range2.max >= range1.min)
          ) {
            const row1Index = range1.rowIndex + 1;
            const row2Index = range2.rowIndex + 1;
            
            // Only show error if it's not already caught by sequential check
            if (Math.abs(range1.rowIndex - range2.rowIndex) > 1) {
              Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: `Range overlap detected! Row ${row1Index} (${range1.min}-${range1.max}) overlaps with Row ${row2Index} (${range2.min}-${range2.max}). Ranges must be non-overlapping.`,
              });
              return;
            }
          }
        }
      }
    }

      // Join categories array with commas for database storage
      const catgCodeString = workflowForm.catgCodes && workflowForm.catgCodes.length > 0 
        ? workflowForm.catgCodes.join(',') 
        : null;

      // Join SR Type codes array with commas for database storage (if it's an array)
      let srTypeCodeString = null;
      if (workflowForm.srTypeCode) {
        if (Array.isArray(workflowForm.srTypeCode)) {
          srTypeCodeString = workflowForm.srTypeCode.length > 0
            ? workflowForm.srTypeCode.join(',')
            : null;
        } else if (typeof workflowForm.srTypeCode === 'string' && workflowForm.srTypeCode.trim() !== '') {
          srTypeCodeString = workflowForm.srTypeCode.trim();
        }
      }
      if (
        ((workflowForm.pageType || pageType) === "ApprovalConfiguration" ||
          (workflowForm.pageType || pageType) === "SLATatConfiguration") &&
        srTypeCodeString &&
        srTypeCodeString.includes(",")
      ) {
        srTypeCodeString = srTypeCodeString.split(",")[0].trim();
      }

      // Prepare workflow data - use finalWorkflowCode to ensure it's set
      const currentPageType = workflowForm.pageType || pageType;
      const marketForApi =
        currentPageType === "ApprovalConfiguration" || currentPageType === "SLATatConfiguration"
          ? workflowForm.marketCodes?.length
            ? [...workflowForm.marketCodes].filter(Boolean).join(",")
            : null
          : workflowForm.marketCode && workflowForm.marketCode !== "0000"
            ? workflowForm.marketCode
            : null;

      const workflowData = {
        workflowCode: finalWorkflowCode || workflowForm.workflowCode,
        workflowName: workflowForm.workflowName,
        SRTYPECODE: srTypeCodeString, // Can be comma-separated string or null
        MARKETCODE: marketForApi,
        CATGCODE: catgCodeString,
        pageType: currentPageType,
        isActive: workflowForm.isActive,
      };
      if (currentPageType === "ApprovalConfiguration" || currentPageType === "SLATatConfiguration") {
        workflowData.workflowSource = JSON.stringify({
          userSpecificationType: workflowForm.userSpecificationType || "None",
        });
      }

      // Prepare levels data - use finalWorkflowCode to ensure it's set
      // Sequence is maintained internally based on row order
      const currentWorkflowCode = finalWorkflowCode || workflowForm.workflowCode;
      const levelsData = rows.map((row, index) => {
        const levelData = {
          approvalLevelCode: row.approvalLevelCode || `${currentWorkflowCode}_LVL${String(index + 1).padStart(3, '0')}`,
          workflowCode: currentWorkflowCode,
          levelSequence: index + 1, // Maintained internally based on row order
          approvalType: (row.approvalType && row.approvalType.trim()) ? row.approvalType : "Required", // Default value maintained internally - always send "Required" if not set
          PLANTCODE: row.plantCode || null, // Default value maintained internally
          USERTYPECODE: row.userRole?.id || row.userRole || null,
          isPlantQCRequired: row.isPlantQCRequired || false, // Default value maintained internally
          isActive: row.isActive !== undefined ? row.isActive : true,
        };
        
        // Only include minValue, maxValue, and currency if pageType is not ApprovalConfiguration or SLATatConfiguration
        if (pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration") {
          levelData.minValue = row.minValue ? parseInt(String(row.minValue).trim(), 10) : null;
          levelData.maxValue = row.maxValue ? parseInt(String(row.maxValue).trim(), 10) : null;
          levelData.currency = row.currency || null;
        }
        
        // For SLATatConfiguration, include row-wise SLA and TAT per level
        if (pageType === "SLATatConfiguration") {
          levelData.slaDays = row.slaDays !== "" && row.slaDays != null ? (isNaN(Number(row.slaDays)) ? null : Number(row.slaDays)) : null;
          levelData.tatDays = row.tatDays !== "" && row.tatDays != null ? (isNaN(Number(row.tatDays)) ? null : Number(row.tatDays)) : null;
        }
        
        return levelData;
      });

      setSaving(true);
      // Call combined save API
      const response = await approvalWorkflowAPI.saveWorkflowWithLevels(workflowData, levelsData, editingWorkflow ? "update" : "create");

      if (response?.success) {
        const savedWorkflowCode = response.data?.workflowCode || workflowData.workflowCode;
        
        // Get pageType from query string to ensure accuracy
        const currentSearchParams = new URLSearchParams(location.search);
        const currentPageType = currentSearchParams.get('pageType') || workflowForm.pageType || pageType || 'ApprovalConfiguration';
        
        Swal.fire({
          icon: "success",
          title: "Success",
          text: editingWorkflow ? "Workflow and levels updated successfully" : "Workflow and levels created successfully",
          confirmButtonText: "OK",
        }).then(() => {
          const targetPath = currentPageType === "SLATatConfiguration" ? "/sla-tat-configuration" : "/dynamic-approval-configuration";
          navigate(targetPath);
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to save workflow and levels",
        });
      }
    } catch (error) {
      console.error("Error saving workflow and levels:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to save workflow and levels",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="approval-level-setup-loading" aria-live="polite" aria-busy="true">
        <CircularProgress size={40} aria-label="Loading configuration" />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {pageType === "SLATatConfiguration"
            ? "Loading SLA & TAT configuration..."
            : "Loading approval configuration..."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="approval-level-setup-container">
      {/* Header Section */}
      <Box className="approval-level-setup-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="approval-level-setup-breadcrumb"
        >
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {pageType === "SLATatConfiguration" ? "SLA & TAT Configuration" : "Approval Configuration"}
          </Typography>
        </Breadcrumbs>
        <Box className="approval-level-setup-header-right">
          <Button
            className="approval-level-setup-back-button"
            onClick={() => (pageType === "SLATatConfiguration" ? navigate("/sla-tat-configuration") : navigate(-1))}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <div className="container-fluid approval-level-setup-page">
        {/* Workflow Form Section - Upper Panel */}
        <div className="card-panel mb-3">
          <div className="section-heading d-flex align-items-center justify-content-between mb-3">
            <Typography variant="h6" className="add-edit-user-section-heading">
              {pageType === "SLATatConfiguration" ? "SLA & TAT Configuration" : "Approval Workflows"}
            </Typography>
          </div>

          {showWorkflowForm && (
            <div className="row g-3 mb-4">
              {/* Workflow Code is hidden but still auto-generated */}
              <input type="hidden" value={workflowForm.workflowCode} />

              {(pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration") && (
                <>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="select2-wrapper">
                      <label className="select2-label pnlLblsze">
                        Market <span style={{ color: "red" }}>*</span>
                      </label>
                      <CustomMultiSelect
                        options={(markets || [])
                          .map((m) => ({
                            id: m.marketCode || m.MARKETCODE || m.MarketCode || "",
                            text:
                              m.marketName ||
                              m.MARKETNAME ||
                              m.MarketName ||
                              m.marketCode ||
                              m.MARKETCODE ||
                              m.MarketCode ||
                              "",
                          }))
                          .filter((o) => o.id)}
                        value={
                          workflowForm.marketCodes?.length
                            ? [String(workflowForm.marketCodes[0]).trim()]
                            : []
                        }
                        onChange={(selected) => {
                          const picked =
                            Array.isArray(selected) && selected.length ? selected[0] : "";
                          const code = picked ? String(picked).trim() : "";
                          setWorkflowForm((prev) => ({
                            ...prev,
                            marketCodes: code ? [code] : [],
                            marketCode: code || "0000",
                            catgCodes: [],
                            srTypeCode: "",
                            userSpecificationType: "",
                          }));
                        }}
                        placeholder="Select Market"
                        disabled={false}
                        showSelectAll={false}
                        singleSelect
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="select2-wrapper">
                      <label className="select2-label pnlLblsze">
                        Business Vertical <span style={{ color: "red" }}>*</span>
                      </label>
                      <CustomMultiSelect
                        options={(businessVerticals || [])
                          .map((c) => ({
                            id: c.catgCode || c.CATGCODE || c.CatgCode || "",
                            text:
                              c.catgName ||
                              c.CATGNAME ||
                              c.CatgName ||
                              c.catgCode ||
                              c.CATGCODE ||
                              "",
                          }))
                          .filter((o) => o.id)}
                        value={workflowForm.catgCodes || []}
                        onChange={(selected) =>
                          setWorkflowForm((prev) => ({
                            ...prev,
                            catgCodes: Array.isArray(selected) ? selected : [],
                          }))
                        }
                        placeholder={
                          !workflowForm.marketCodes?.length
                            ? "Select Market first"
                            : "Select Business Vertical"
                        }
                        disabled={!workflowForm.marketCodes?.length}
                        showSelectAll
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="select2-wrapper">
                      <label className="select2-label pnlLblsze">SR Type</label>
                      <CustomMultiSelect
                        options={srTypeOptions || []}
                        value={(() => {
                          const v = workflowForm.srTypeCode;
                          if (v == null || v === "") return [];
                          const code = Array.isArray(v)
                            ? String(v[0] || "").trim()
                            : String(v).split(",")[0].trim();
                          return code ? [code] : [];
                        })()}
                        onChange={(selected) => {
                          const picked =
                            Array.isArray(selected) && selected.length ? selected[0] : "";
                          setWorkflowForm((prev) => ({
                            ...prev,
                            srTypeCode: picked ? String(picked).trim() : "",
                          }));
                        }}
                        placeholder={
                          !workflowForm.marketCodes?.length
                            ? "Select Market first"
                            : "Select SR Type"
                        }
                        disabled={!workflowForm.marketCodes?.length}
                        showSelectAll={false}
                        singleSelect
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="select2-wrapper">
                      <label className="select2-label pnlLblsze">
                        SR Journey Flow <span style={{ color: "red" }}>*</span>
                      </label>
                      <CustomMultiSelect
                        options={approvalUserSpecOptions}
                        value={
                          workflowForm.userSpecificationType &&
                          workflowForm.userSpecificationType !== "None"
                            ? [workflowForm.userSpecificationType]
                            : []
                        }
                        onChange={(selected) => {
                          const picked =
                            Array.isArray(selected) && selected.length ? selected[0] : "";
                          setWorkflowForm((prev) => ({
                            ...prev,
                            userSpecificationType: picked || "",
                          }));
                        }}
                        placeholder={
                          !workflowForm.marketCodes?.length
                            ? "Select Market first"
                            : "Select SR Journey Flow"
                        }
                        disabled={!workflowForm.marketCodes?.length || !approvalUserSpecOptions.length}
                        showSelectAll={false}
                        singleSelect
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="textfield-wrapper wrkflwPnl">
                      <label className="select2-label pnlLblsze">
                        Workflow Name <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        value={workflowForm.workflowName}
                        onChange={(e) =>
                          setWorkflowForm({ ...workflowForm, workflowName: e.target.value })
                        }
                        required
                        fullWidth
                        size="small"
                        placeholder="Enter Workflow Name"
                        className="add-edit-user-textfield"
                      />
                    </div>
                  </div>
                </>
              )}

              {pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration" && (
              <>
              <div className="col-12 col-md-6 col-lg-3">
                <div className="textfield-wrapper wrkflwPnl">
                  <label className="select2-label pnlLblsze">
                    Workflow Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={workflowForm.workflowName}
                    onChange={(e) =>
                      setWorkflowForm({ ...workflowForm, workflowName: e.target.value })
                    }
                    required
                    fullWidth
                    size="small"
                    placeholder="Enter Workflow Name"
                    className="add-edit-user-textfield"
                  />
                </div>
              </div>

                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="textfield-wrapper">
                      <label className="select2-label pnlLblsze">
                        Market <span style={{ color: "red" }}>*</span>
                      </label>
                      <select
                        ref={marketSelectRef}
                        className="form-select form-control-light filter-select text-start"
                        style={{ height: "40px", textAlign: "left" }}
                      >
                        <option value="0000">Select Market</option>
                        {markets && markets.length > 0 ? (
                          markets.map((market) => {
                            const code = market.marketCode || market.MarketCode || "";
                            const name = market.marketName || market.MarketName || "";
                            return (
                              <option key={code} value={code}>
                                {name}
                              </option>
                            );
                          })
                        ) : (
                          <option value="0000" disabled>Loading markets...</option>
                        )}
                      </select>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="textfield-wrapper">
                      <label className="select2-label pnlLblsze">
                        Product Category <span style={{ color: "red" }}>*</span>
                      </label>
                      <select
                        ref={catgSelectRef}
                        className="form-select form-control-light filter-select text-start category-multiselect"
                        style={{ height: "40px", textAlign: "left" }}
                        multiple
                        disabled={!workflowForm.marketCode || workflowForm.marketCode === "0000"}
                      >
                        {categories && categories.length > 0 ? (
                          categories.map((cat) => {
                            const code = cat.catgCode || cat.CatgCode || "";
                            const name = cat.catgName || cat.CatgName || "";
                            return (
                              <option key={code} value={code}>
                                {name}
                              </option>
                            );
                          })
                        ) : (
                          <option value="" disabled>
                            {!workflowForm.marketCode || workflowForm.marketCode === "0000"
                              ? "Please select Market first"
                              : "Loading categories..."}
                          </option>
                        )}
                      </select>
                    </div>
                  </div>
              <div className="col-12 col-md-6 col-lg-3">
                <div className="select2-wrapper">
                  <label className="select2-label pnlLblsze">SR Type</label>
                    <CustomMultiSelect
                      options={srTypeOptions || []}
                      value={
                        Array.isArray(workflowForm.srTypeCode)
                          ? workflowForm.srTypeCode
                          : workflowForm.srTypeCode
                          ? String(workflowForm.srTypeCode)
                              .split(",")
                              .map((c) => c.trim())
                              .filter((c) => c)
                          : []
                      }
                      onChange={(selected) =>
                        setWorkflowForm((prev) => ({ ...prev, srTypeCode: selected }))
                      }
                      placeholder={
                        !workflowForm.marketCode || workflowForm.marketCode === "0000"
                          ? "Select Market first"
                          : "Select SR Type"
                      }
                      disabled={!workflowForm.marketCode || workflowForm.marketCode === "0000"}
                      showSelectAll
                      selectAllLabel="Select all"
                    />
                </div>
              </div>
              </>
              )}
            </div>
          )}
          
        </div>

        {/* Level Setup Section */}
        <div className="card-panel approval-level-panel">
          <Typography variant="h6" className="section-heading">
            Approval Configuration Levels
          </Typography>

          {/* Rows Container */}
          <div
            className={`approval-level-rows${
              insertingRowAfterIndex !== null ? " approval-level-rows--inserting" : ""
            }`}
          >
            {rows.map((row, index) => (
              <div
                key={row.id}
                className={`approval-level-row ${
                  dragOverIndex === index ? "drag-over" : ""
                } ${draggedIndex === index ? "dragging" : ""}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                {/* Drag Handle */}
                <div className="drag-handle">
                  <DragIndicatorIcon className="drag-icon" />
                </div>

                {/* Row Content */}
                <div className="row-content">
                  <div className="row g-3">
                    {/* User Role Select */}
                    <div className="col-12 col-md-3">
                      <div className="select2-wrapper select2-wrapper--with-loader">
                        <label className="select2-label pnlLblsze">
                          User Role
                        </label>
                        {validatingRoleRowId === row.id && (
                          <CircularProgress
                            size={18}
                            className="user-role-field-loader"
                            aria-label="Validating user role"
                          />
                        )}
                        <Select2Dropdown
                          defaultOptions={
                            pageType === "ApprovalConfiguration" || pageType === "SLATatConfiguration"
                              ? userRoleOptions
                              : userRoleOptions.filter((role) => {
                                  const selectedRoleIds = rows
                                    .filter(r => r.id !== row.id && r.userRole)
                                    .map(r => {
                                      const roleValue = r.userRole;
                                      if (typeof roleValue === 'string') {
                                        return roleValue.trim();
                                      } else if (typeof roleValue === 'object' && roleValue !== null) {
                                        return roleValue.id || roleValue.userTypeCode || String(roleValue);
                                      } else if (typeof roleValue === 'number') {
                                        return String(roleValue);
                                      }
                                      return null;
                                    })
                                    .filter(id => id && id !== '' && id !== '0000');
                                  const roleId = String(role.id || role.userTypeCode || '');
                                  return !selectedRoleIds.includes(roleId);
                                })
                          }
                          loadOptions={(searchText, pageNumber, pageSize) => 
                            loadRoleOptions(searchText, pageNumber, pageSize, row.id)
                          }
                          value={row.userRole}
                          onChange={(value) =>
                            handleRowChange(row.id, "userRole", value)
                          }
                          disabled={validatingRoleRowId === row.id}
                          placeholder="Select User Role"
                          getOptionLabel={(option) => option?.name || String(option)}
                          getOptionValue={(option) => option?.id || option}
                          pageSize={20}
                          minSearchLength={0}
                          allowClear={true}
                        />
                      </div>
                    </div>

                    {/* TAT (Days) - Row-wise for SLATatConfiguration in Approval Configuration Levels */}
                    {pageType === "SLATatConfiguration" && (
                        <div className="col-12 col-md-2">
                          <div className="textfield-wrapper">
                            <label className="select2-label pnlLblsze">TAT (Days)</label>
                            <TextField
                              type="number"
                              value={row.tatDays ?? ""}
                              onChange={(e) =>
                                handleRowChange(row.id, "tatDays", e.target.value)
                              }
                              fullWidth
                              size="small"
                              placeholder="TAT in days"
                              className="add-edit-user-textfield"
                              inputProps={{ min: 0, step: 1 }}
                            />
                          </div>
                        </div>
                    )}

                    {/* Minimum Value Input - Hidden for ApprovalConfiguration and SLATatConfiguration */}
                    {pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration" && (
                      <div className="col-12 col-md-2">
                        <div className="textfield-wrapper">
                          <label className="select2-label pnlLblsze">
                            Min Value
                          </label>
                          <TextField
                            type="number"
                            value={row.minValue}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Remove any decimal point and everything after it
                              if (value.includes('.')) {
                                value = value.split('.')[0];
                              }
                              // Remove any non-digit characters except empty string
                              if (value !== '' && !/^-?\d+$/.test(value)) {
                                value = value.replace(/[^\d-]/g, '');
                              }
                              handleRowChange(row.id, "minValue", value);
                            }}
                            onBlur={() => {
                              // Trigger validation on blur
                              const validation = validateRowRange(
                                row.id,
                                row.minValue,
                                row.maxValue,
                                rows
                              );
                              setRows((prevRows) =>
                                prevRows.map((r) =>
                                  r.id === row.id
                                    ? { ...r, rangeError: validation.error, rangeValid: validation.isValid }
                                    : r
                                )
                              );
                            }}
                            fullWidth
                            size="small"
                            placeholder="Min"
                            className="add-edit-user-textfield"
                            error={row.rangeError && !row.rangeValid}
                            helperText={row.rangeError && !row.rangeValid ? row.rangeError : ""}
                            inputProps={{ step: "1", min: "0" }}
                            onKeyDown={(e) => {
                              // Prevent decimal point and other non-integer characters
                              if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Maximum Value Input - Hidden for ApprovalConfiguration */}
                    {pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration" && (
                      <div className="col-12 col-md-2">
                        <div className="textfield-wrapper">
                          <label className="select2-label pnlLblsze">
                            Max Value
                          </label>
                          <TextField
                            type="number"
                            value={row.maxValue}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Remove any decimal point and everything after it
                              if (value.includes('.')) {
                                value = value.split('.')[0];
                              }
                              // Remove any non-digit characters except empty string
                              if (value !== '' && !/^-?\d+$/.test(value)) {
                                value = value.replace(/[^\d-]/g, '');
                              }
                              handleRowChange(row.id, "maxValue", value);
                            }}
                            onBlur={() => {
                              // Trigger validation on blur
                              const validation = validateRowRange(
                                row.id,
                                row.minValue,
                                row.maxValue,
                                rows
                              );
                              setRows((prevRows) =>
                                prevRows.map((r) =>
                                  r.id === row.id
                                    ? { ...r, rangeError: validation.error, rangeValid: validation.isValid }
                                    : r
                                )
                              );
                            }}
                            fullWidth
                            size="small"
                            placeholder="Max"
                            className="add-edit-user-textfield"
                            error={row.rangeError && !row.rangeValid}
                            helperText={row.rangeError && !row.rangeValid ? row.rangeError : ""}
                            inputProps={{ step: "1", min: "0" }}
                            onKeyDown={(e) => {
                              // Prevent decimal point and other non-integer characters
                              if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Currency Input - Hidden for ApprovalConfiguration */}
                    {pageType !== "ApprovalConfiguration" && pageType !== "SLATatConfiguration" && (
                      <div className="col-12 col-md-2">
                        <div className="textfield-wrapper">
                          <label className="select2-label pnlLblsze">
                            Currency
                          </label>
                          <TextField
                            value={row.currency || "INR"}
                            onChange={(e) =>
                              handleRowChange(
                                row.id,
                                "currency",
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                            placeholder="INR"
                            className="add-edit-user-textfield"
                          />
                        </div>
                      </div>
                    )}

                    {/* Active/Inactive Toggle */}
                    <div className="col-12 col-md-2">
                      <div className="toggle-wrapper">
                        <label className="select2-label pnlLblsze">
                          Status
                        </label>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={row.isActive}
                              onChange={(e) =>
                                handleRowChange(
                                  row.id,
                                  "isActive",
                                  e.target.checked
                                )
                              }
                              color="primary"
                            />
                          }
                          label={row.isActive ? "Active" : "Inactive"}
                          className="toggle-label"
                        />
                      </div>
                    </div>

                    {/* Row Actions */}
                    <div className="col-12 col-md-1">
                      <div className="row-actions">
                        <Button
                          className="row-action-btn add-btn"
                          onClick={() => handleInsertRow(index)}
                          title="Insert row below"
                          size="small"
                          disabled={insertingRowAfterIndex !== null}
                        >
                          {insertingRowAfterIndex === index ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <AddIcon fontSize="small" />
                          )}
                        </Button>
                        <Button
                          className="row-action-btn remove-btn"
                          onClick={() => handleDeleteRow(row.id)}
                          disabled={rows.length <= 1 || insertingRowAfterIndex !== null}
                          title="Delete row"
                          size="small"
                        >
                          <RemoveIcon fontSize="small" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info Message and Action Buttons */}
          <div className="approval-level-info-container">
            <div className="approval-level-info">
              <Typography variant="body2" className="info-text fw-bold">
                Drag rows to reorder • Click <span className="text-success">&#65291;</span> to insert a row below • Click <span className="text-danger"> &ndash; </span> to
                delete a row
              </Typography>
            </div>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => (pageType === "SLATatConfiguration" ? navigate("/sla-tat-configuration") : navigate("/dynamic-approval-configuration"))}
                className="btn-cancel-user funbtns"
                startIcon={<CancelIcon />}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                className={`approval-level-save-button btn-save-user funbtns${
                  saving ? " is-saving" : ""
                }`}
                onClick={handleSave}
                disabled={saving || loading}
                aria-busy={saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={18} thickness={5} sx={{ color: "inherit" }} />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default DynamicApprovalConfigurationLevelSetup;

